import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  FileText,
  Award,
  MessageSquare,
  BarChart3,
  CheckCircle,
  CheckCircle2,
  Users,
  Search,
  Play,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Target,
  Heart,
  TrendingUp,
  UserX,
  Loader2,
  AlertCircle,
  Send,
  ThumbsUp,
  Save,
  HeartPulse,
  Star,
  Activity,
  MessageCircle,
  XCircle,
  Info,
  Clock,
  CalendarRange,
  History,
} from 'lucide-react';
import NarrativeCompilationPanel from './components/NarrativeCompilationPanel';
import SurfedStrengthsSkillsPanel from './components/SurfedStrengthsSkillsPanel';
import NarrativePromptsPanel, { NARRATIVE_PROMPT_QUESTION_KEYS } from './components/NarrativePromptsPanel';
import ComparativeContextPanel from './components/ComparativeContextPanel';
import DecisionOutputsPanel from './components/DecisionOutputsPanel';
import SnapshotsAnalysisPanel from './components/SnapshotsAnalysisPanel';
import {
  generateSnapshot,
  getEmployeeSnapshots,
  getSnapshotRoster,
  updateSnapshot,
  submitSnapshot,
  acknowledgeSnapshot,
  normalizeSnapshotStatus,
  formatPeriodLabel,
  comparePeriodsDesc,
  isRawPeriodToken,
  isMonthPeriod,
  parseSignalsSummary,
  PerformanceSnapshotView,
  SignalsSummary,
  SnapshotStatus,
  SnapshotUpdateInput,
} from '../../api/PreformanceManagement/PerformanceSnapshot';
import YearMonthPicker from '../../components/yearMonthPicker';
import { appDate } from '../../utils/appDateFormat';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { usePageTabConfig } from '../../hooks/usePageTabConfig';
import { PERFORMANCE_SNAPSHOT_TAB_MODULE_ID } from '../../components/layout/navigationModules';
import { parseApiUtc } from '@/utils/apiDate';

export interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  manager: string;
  lastReviewDate: string;
  /** "2026-07" / "2026-Q3" — the period of the most recent snapshot, blank when there is none. */
  lastReviewPeriod: string;
  nextReviewDue: string;
  imageUrl?: string | null;
}

export interface ReviewData {
  employeeId: string;
  generatedAt: string;
  snapshotId: number | null;
  snapshot: PerformanceSnapshotView | null;
  narrative: Record<string, unknown> | null;
  /**
   * CO-434 — the HR VALIDATION of the surfaced strengths ({validations:[…]}), NOT the skills
   * summary. It used to be seeded from `strengthsSummary`, so simply opening the tab and saving
   * wrote `{topSkills:[…]}` over the validation column. The skills summary is read straight off
   * `snapshot.strengthsSummary` for display and never round-trips.
   */
  strengths: Record<string, unknown> | null;
  prompts: Record<string, string> | null;
  comparative: Record<string, unknown> | null;
  decisions: Record<string, unknown> | null;
}

/**
 * TOP-LEVEL SECTIONS of this page. Owner decision 2026-08-21: the page has exactly TWO of them,
 * and they are what the Operator toggles in Module Config — not the five inner panels.
 *   'employee-snapshot'  everything the page did before: pick an employee, generate, then the
 *                        five inner tabs (narrative / strengths / prompts / comparative / decisions).
 *   'snapshots-analysis' org-wide coverage built from PAST SNAPSHOTS. Needs no selected employee,
 *                        so the employee picker is hidden while it is active.
 */
const SECTION_EMPLOYEE = 'employee-snapshot';
const SECTION_ANALYSIS = 'snapshots-analysis';

/**
 * CO-437 — who may AUTHOR a snapshot. Hr/Admin author for anyone, a people-manager for their own
 * direct reports. Everyone else gets employee mode: their own snapshots, read-only, with an
 * Acknowledge button. A manager's primary Identity role is usually "Clocking", so manager status
 * comes from the `isManager` flag rather than the role string (same shape as RoleProtectedRoute).
 * This gate is UX only; the server remains the security boundary.
 */
const AUTHORING_ROLES = ['Hr', 'Admin'];

/** CO-433 — how long typing pauses before the page writes. Long enough not to save mid-word. */
const AUTOSAVE_DEBOUNCE_MS = 800;

/* ──────────────────────────────────────────────────────────────────────────────────────────────
   CO-439 — the month picker adapter.

   🔴 TWO DIFFERENT TIME BASES MEET HERE, and mixing them is an off-by-one-month bug that reads as
   a working feature. `YearMonthPicker` speaks a LOCAL `Date` throughout — it reads `getMonth()` /
   `getFullYear()` and writes `setMonth(m, 1)` at local midnight. The `yyyy-MM` token is a UTC month
   on the server. So:

     • `toPeriod` / `fromPeriod` use LOCAL parts, because their only job is to round-trip through
       the picker. `Date.UTC(2026, 6, 1)` is 2026-06-30T21:00 local in Istanbul (UTC+3), whose
       `getUTCMonth()` is JUNE — a picker that reads back one month behind every selection.
     • Every token we INVENT (today's month, the default) is derived from UTC parts, because the
       server resolves its own default with `DateTime.UtcNow`. At a month boundary a browser behind
       UTC would otherwise offer a different default month than the one the server would pick.

   The period string is what crosses the wire; a `Date` never does.
   ────────────────────────────────────────────────────────────────────────────────────────────── */

const pad2 = (n: number) => String(n).padStart(2, '0');

/** The picker's local `Date` → `yyyy-MM`. */
const toPeriod = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

/** `yyyy-MM` → the local `Date` (day 1, local midnight) the picker speaks. */
const fromPeriod = (p: string) => {
  const [year, month] = p.split('-');
  return new Date(Number(year), Number(month) - 1, 1);
};

/** Today's month in UTC — the picker's upper bound, so no FUTURE month is selectable. */
const currentMonthPeriod = (now: Date = new Date()) => `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}`;

/** Month arithmetic on the token itself. UTC in, UTC out, so no local boundary can shift it. */
const shiftPeriod = (period: string, deltaMonths: number) => {
  const [year, month] = period.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + deltaMonths, 1));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}`;
};

/**
 * The DEFAULT selection, and it must be LAST COMPLETED, never current: a current-month default
 * makes every fresh snapshot partial, and an in-progress low count read as poor performance is the
 * whole class CO-443 exists to close.
 */
const lastCompletedMonthPeriod = () => shiftPeriod(currentMonthPeriod(), -1);

/**
 * CO-436/CO-437/CO-439 — is this refusal PERMANENT?
 *
 * Measured on the deployed build: `handleGenerateReview`'s bare `catch` printed "Failed to generate
 * snapshot. Please try again." for BOTH 403s — self-authoring ("you cannot author your own
 * performance snapshot") and out-of-scope ("you can only work on snapshots for your own direct
 * reports"). The server wrote two accurate, deliberately worded sentences and neither ever reached
 * the user, while "try again" invited a manager to retry something that can never succeed and hid
 * the separation-of-duties rule CO-436/CO-437 exist to teach.
 *
 * 403 (refused) and 400 (this month is invalid or in the future) are both permanent for the request
 * as sent. Everything else — a 5xx, a dropped connection — is genuinely worth retrying.
 *
 * The status arrives on `__httpStatus`, stamped non-enumerably by the axios error interceptor
 * (`utils/requestHandling.ts`), because an ASP.NET authorization denial has an EMPTY body and the
 * bare rejection would otherwise carry no status at all. Same read as `views/UserProfile/index.tsx`.
 */
const isPermanentRefusal = (err: unknown): boolean => {
  const e = (err ?? {}) as { __httpStatus?: number; status?: number; response?: { status?: number } };
  const status = e.__httpStatus ?? e.response?.status ?? e.status;
  return status === 403 || status === 400;
};

/**
 * CO-433 — `'pending'` is the state between a keystroke and the debounced write. It exists because
 * `'idle'` renders NOTHING: a manager who typed and navigated away inside the 800 ms window had no
 * way to know a write was still owed, and the indicator could never be caught lying about it.
 */
type SaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

/**
 * CO-436/CO-437 — an error the page shows, and whether retrying it could ever work.
 *
 * `permanent` is not cosmetic. It is what keeps the banner from telling a manager to retry a
 * refusal the server will give forever, and it is asserted on: the banner carries
 * `data-permanent` so a test can discriminate the two without reading pixels.
 */
type PageError = { message: string; permanent: boolean };

const STATUS_BADGE: Record<SnapshotStatus, string> = {
  Draft: 'bg-amber-50 text-amber-700 border border-amber-200',
  Submitted: 'bg-blue-50 text-blue-700 border border-blue-200',
  Acknowledged: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const STATUS_LABEL_KEY: Record<SnapshotStatus, string> = {
  Draft: 'performanceSnapshot.statusDraft',
  Submitted: 'performanceSnapshot.statusSubmitted',
  Acknowledged: 'performanceSnapshot.statusAcknowledged',
};

interface UserSliceShape {
  userId: string;
  role: string;
  isManager: boolean;
}

const safeParse = (json: string | null | undefined): Record<string, unknown> | null => {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * CO-433 — the ONE save payload. Every writer on this page (autosave, Save Draft, Submit) builds it
 * here, so there is exactly one definition of what "saved" means. Null members are omitted rather
 * than sent as `null`: an untouched section must not blank a column the server already holds.
 */
const buildUpdateInput = (data: ReviewData): SnapshotUpdateInput => {
  const input: SnapshotUpdateInput = {};
  if (data.decisions) input.decisionsSummary = JSON.stringify(data.decisions);
  if (data.prompts) input.narrativePromptsSummary = JSON.stringify(data.prompts);
  if (data.strengths) input.strengthsValidation = JSON.stringify(data.strengths);
  if (data.narrative) input.narrativeSummary = JSON.stringify(data.narrative);
  return input;
};

/**
 * CO-433 — the page's ONLY save feedback. It replaces the two in-panel "Saved" toasts, which
 * confirmed a write that never left the browser. Idle renders nothing: a permanent "Saved" chip on
 * an untouched page is noise, and it makes the real transition invisible.
 */
const SaveIndicator = ({ state, onRetry }: { state: SaveState; onRetry: () => void }) => {
  const { t } = useTranslation();
  if (state === 'idle') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="snapshot-save-state"
      data-save-state={state}
      className="flex items-center gap-1.5 text-xs"
    >
      {state === 'pending' && (
        <>
          <Clock size={14} className="text-amber-600" />
          <span className="text-muted-foreground">{t('performanceSnapshot.autosave.pending')}</span>
        </>
      )}
      {state === 'saving' && (
        <>
          <Loader2 size={14} className="animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">{t('performanceSnapshot.autosave.saving')}</span>
        </>
      )}
      {state === 'saved' && (
        <>
          <CheckCircle2 size={14} className="text-emerald-600" />
          <span className="text-muted-foreground">{t('performanceSnapshot.autosave.saved')}</span>
        </>
      )}
      {state === 'error' && (
        <>
          <AlertCircle size={14} className="text-red-600" />
          <span className="text-red-700">{t('performanceSnapshot.autosave.failed')}</span>
          <button
            type="button"
            onClick={onRetry}
            className="font-medium text-red-700 underline underline-offset-2 hover:text-red-800"
          >
            {t('performanceSnapshot.autosave.retry')}
          </button>
        </>
      )}
    </div>
  );
};

/**
 * CO-439 — "this month is not finished". Rendered beside the picker while the CURRENT month is
 * selected, and again on the snapshot's window line while the snapshot's PERIOD is the current
 * month. 🔴 Not while `signals.isInProgress` — that flag is frozen at generation time, so reading
 * it here left the chip on a month that had been over for weeks. See `SnapshotSummaryCard`.
 *
 * The server has already clamped every source query to "never read the future", so a snapshot
 * generated on the 8th covers the 1st–8th. The chip is what stops that partial month being read as
 * poor performance — a low count with no explanation is indistinguishable from a bad month.
 *
 * 🔴 CO-431 — a palette colour, never `bg-primary/10`: Tailwind 3.4.19 cannot compute an alpha
 * channel for a bare `var()` token, so an opacity modifier on one emits no colour at all.
 */
const InProgressChip = () => {
  const { t } = useTranslation();
  return (
    <span
      data-testid="snapshot-in-progress-chip"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
    >
      <Clock size={12} />
      {t('performanceSnapshot.periodPicker.inProgress')}
    </span>
  );
};

/**
 * CO-443 — ONE tile. Every tile object carries EXACTLY these five keys.
 *
 * 🔴 The identical-keys rule is a typecheck-gate requirement, not a style preference: an array
 * whose branches return different key sets becomes a union type and fails `npm run typecheck`
 * (`scripts/tsc-baseline.mjs`, which allows the baseline to go down and never up). Unused slots
 * are `null`, never omitted.
 */
type SignalTile = {
  key: string;
  label: string;
  /** The headline number, already formatted. `null` means UNMEASURED — never render a 0 for it. */
  primary: string | null;
  /** What the headline is out of. `null` when there is nothing honest to say. */
  denominator: string | null;
  measured: boolean;
  /**
   * CO-443 — the THIRD state, and it exists because the other two were being conflated.
   *
   * `measured` is a statement about the COMPANY (nobody produced a record of this kind), and the
   * unmeasured tile's only copy says exactly that. The goals tile used to AND `total > 0` into it,
   * so an employee who simply had no goals landing in the month rendered "Not enough signal" over
   * the sentence "Nobody in the company recorded this kind of activity in this period" — a claim
   * about the company that the server had already computed to be FALSE, printed on a record the
   * employee acknowledges. In any real tenant that is the majority case.
   *
   * `true` means: the company DID record this kind of activity, this person had none of it, and
   * there is no honest denominator to divide by (`0 / 0` is not a score). It renders as its own
   * quiet tile — never as a low score, and never as a claim about anybody but this person.
   */
  noneInPeriod: boolean;
};

/**
 * CO-443 — the tile itself.
 *
 * 🔴 CO-431: NO Tailwind opacity modifier on a `var()`-backed token here. `tailwind.config.cjs:22`
 * maps `primary.DEFAULT: 'var(--color-primary)'` and Tailwind 3.4.19 cannot compute an alpha
 * channel for a bare `var()` colour, so `bg-primary/10` / `bg-muted/50` emit NO COLOUR AT ALL —
 * measured, pixel-sampled. Solid tokens (`bg-muted`, `bg-card`) are fine; palette colours are fine.
 * A DOM class assertion passes on a tile that renders transparent, so these are verified by pixel.
 *
 * The unmeasured tile is deliberately an OUTLINE, not a filled tile with a zero in it: it has to
 * read as "we did not measure this", never as a low score. No red, no zero, no bar at 0%.
 *
 * 🔴 THREE STATES, and the first two must never be collapsed into one. `notEnoughHint` makes a
 * statement about the COMPANY; `noneInPeriodHint` makes one about this PERSON. Routing a person's
 * empty month through the company-wide sentence prints something untrue on a performance record —
 * see the `noneInPeriod` comment on `SignalTile`.
 */
const SignalTileCard = ({ tile }: { tile: SignalTile }) => {
  const { t } = useTranslation();

  if (!tile.measured) {
    return (
      <div
        data-testid={`snapshot-signal-${tile.key}`}
        data-measured="false"
        className="bg-card border border-dashed border-border rounded-lg p-4 text-center"
      >
        <div className="text-sm font-medium text-muted-foreground">
          {t('performanceSnapshot.signals.notEnough')}
        </div>
        <div className="text-xs text-muted-foreground mt-1">{tile.label}</div>
        <div className="text-xs text-muted-foreground mt-2 leading-snug">
          {t('performanceSnapshot.signals.notEnoughHint')}
        </div>
      </div>
    );
  }

  if (tile.noneInPeriod) {
    // MEASURED, and the measurement is "none". Same quiet outline as the unmeasured tile — this is
    // still not a score — but `data-measured="true"` and a sentence that is only about this person.
    return (
      <div
        data-testid={`snapshot-signal-${tile.key}`}
        data-measured="true"
        data-none-in-period="true"
        className="bg-card border border-dashed border-border rounded-lg p-4 text-center"
      >
        <div className="text-sm font-medium text-muted-foreground">
          {t('performanceSnapshot.signals.noneInPeriod')}
        </div>
        <div className="text-xs text-muted-foreground mt-1">{tile.label}</div>
        <div className="text-xs text-muted-foreground mt-2 leading-snug">
          {t('performanceSnapshot.signals.noneInPeriodHint')}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`snapshot-signal-${tile.key}`}
      data-measured="true"
      className="bg-muted rounded-lg p-4 text-center"
    >
      <div className="text-2xl font-semibold text-foreground tabular-nums" dir="ltr">
        {tile.primary}
      </div>
      <div className="text-xs font-medium text-foreground mt-1">{tile.label}</div>
      {tile.denominator && (
        <div className="text-xs text-muted-foreground mt-1 leading-snug">{tile.denominator}</div>
      )}
    </div>
  );
};

/**
 * CO-442 / CO-443 — the snapshot summary, shared by the manager view and the employee's read-only
 * view. Two things changed here and they are the point of both steps:
 *
 * 1. THE WINDOW IS STATED, ONCE. "July 2026 · 6 goals, 3 check-ins, 7 recognitions in month".
 *    🔴 Tile captions therefore describe WHAT is counted and never OVER HOW LONG. The caption
 *    `performanceSnapshot.checkInsCompleted` read "Check-ins (6 months)" and was rendered on cards
 *    headed "Period: 2026-Q3" AND on `2026-07` cards — the number and its caption describing
 *    different windows, in the same eyeline, visible to the employee. It is gone. Do not
 *    reintroduce a window word into any caption on this card; the window line is the only place
 *    a window is named, and for a legacy row it is suppressed entirely.
 *
 * 2. THE COMPOSITE IS GONE. `overallScore` survives on the wire (the comparative panel's buckets
 *    run over a cohort of pre-CO-443 rows) but it is no longer the headline, because it was
 *    computed by averaging four sources that could all be empty: an employee with NO DATA AT ALL
 *    rendered "25% Overall Score", the model wrote that up as a performance failing, and it was
 *    submitted to and acknowledged by a real person.
 */
const SnapshotSummaryCard = ({ snapshot }: { snapshot: PerformanceSnapshotView }) => {
  const { t, i18n } = useTranslation();
  // `null` is the LEGACY-ROW signal — a snapshot written before the column existed. It selects the
  // retained `overallScore` tile below; it must never be normalised into four zeroes.
  const signals: SignalsSummary | null = parseSignalsSummary(snapshot.signalsSummary);
  // CO-513 — the legacy quarterly path is retired, so this is no longer "is it a quarter" but the
  // shape-agnostic "is this a period we cannot name a month for". A surviving pre-CO-439 `2026-Q3`
  // row still lands here; so would any other unreadable token.
  const unreadablePeriod = isRawPeriodToken(snapshot.period);
  const monthLabel = formatPeriodLabel(snapshot.period, i18n.language);

  /**
   * CO-439 — "in progress" is a fact about the CLOCK, so it is derived at RENDER time.
   *
   * `signals.isInProgress` is frozen into the column at GENERATION time (`p.IsInProgress(now)`),
   * and a Submitted or Acknowledged snapshot is returned untouched forever after. So a snapshot
   * generated on the 21st kept showing the amber chip and "This month is not finished. These
   * numbers will change." months after that month closed — about numbers that by then could not
   * change at all, on a record the employee had already signed.
   *
   * The stored flag is NOT discarded: it is the only record that the window was cut short, and
   * that stays true after the month ends. Once the month is over it captions the numbers with the
   * date they were counted to, instead of promising a future that has already happened.
   */
  const isCurrentMonth = isMonthPeriod(snapshot.period) && snapshot.period === currentMonthPeriod();
  const inProgressNow = signals != null && isCurrentMonth;
  // Suppressed when the period is unreadable: a pre-CO-439 row's window was a ROLLING SIX MONTHS,
  // so "counted up to" would attach a month's caveat to numbers that never measured a month. The
  // hint below already says the window cannot be named, and that is the only claim made about it.
  const countedToPartialWindow = signals?.isInProgress === true && !isCurrentMonth && !unreadablePeriod;
  const countedToLabel = (() => {
    if (!countedToPartialWindow || !signals?.windowEndUtc) return '';
    const end = (parseApiUtc(signals.windowEndUtc) ?? new Date(NaN));
    if (Number.isNaN(end.getTime())) return '';
    // UTC, like every other date on this card: the window bound is a UTC instant on the server.
    // Re-built from the UTC fields so the formatter (local fields) prints the UTC calendar day.
    return appDate(new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  })();

  // Legacy fallback only — a pre-CO-443 row has no `signalsSummary`, but it does still carry the
  // three raw source summaries, and dropping them would lose real information off an old record.
  const legacyGoals = safeParse(snapshot.goalsSummary) as unknown as { completed?: number; total?: number } | null;
  const legacyCheckIns = safeParse(snapshot.checkInsSummary) as unknown as { total?: number } | null;
  const legacyRecognition = safeParse(snapshot.recognitionSummary) as unknown as { received?: number } | null;

  /**
   * 🔴 `0 / 0` IS NOT A SCORE. A zero denominator renders "Not enough signal", never `0`, never a
   * percentage, never a bar — that pairing ("0/0 Goals Completed" next to "25% Overall Score") is
   * exactly what an AI narrative turned into "a notable lack of measurable achievement".
   *
   * `measured` itself is the server's company-wide statement: nobody in the company produced a
   * record of this kind this period. A company that DID and a person who has none is a genuine
   * measurement and renders as `0`.
   */
  const tiles: SignalTile[] = signals
    ? [
        {
          key: 'goals',
          label: t('performanceSnapshot.signals.goals'),
          primary: `${signals.goals.completed} / ${signals.goals.total}`,
          denominator: t('performanceSnapshot.signals.goalsDenominator'),
          /**
           * 🔴 The server's flag is HONOURED, not overridden. This read
           * `signals.goals.measured && signals.goals.total > 0`, which turned "the company
           * recorded goals, this person had none" into the UNMEASURED tile — whose only copy
           * asserts that nobody in the company recorded a goal this period. The server had
           * already computed that to be false, with a company-scoped probe that exists for
           * exactly this distinction.
           *
           * `total === 0` is still a ZERO DENOMINATOR and still must not render `0 / 0` as a
           * score — that is what `noneInPeriod` below is for.
           */
          measured: signals.goals.measured,
          noneInPeriod: signals.goals.measured && signals.goals.total === 0,
        },
        {
          key: 'checkins',
          label: t('performanceSnapshot.signals.checkIns'),
          primary: `${signals.checkIns.completed}`,
          denominator: t('performanceSnapshot.signals.checkInsDenominator', {
            count: signals.checkIns.withContent,
          }),
          measured: signals.checkIns.measured,
          // A plain COUNT has no denominator to be zero, so `0` here is an honest measurement and
          // renders as one. Only the goals ratio can be `0 / 0`. Same for the two tiles below.
          noneInPeriod: false,
        },
        {
          key: 'recognition',
          label: t('performanceSnapshot.signals.recognition'),
          primary: `${signals.recognition.received}`,
          // `distinctSenders` is deliberate and it is the whole point of this tile: the old
          // composite scored recognition as `(received + given) × 10`, which two colleagues could
          // max out in an afternoon by recognising each other. "From 3 people" cannot be.
          denominator: t('performanceSnapshot.signals.recognitionDenominator', {
            senders: t('performanceSnapshot.signals.recognitionSenders', {
              count: signals.recognition.distinctSenders,
            }),
            given: t('performanceSnapshot.signals.recognitionGiven', {
              count: signals.recognition.given,
            }),
          }),
          measured: signals.recognition.measured,
          noneInPeriod: false,
        },
        {
          key: 'skills',
          label: t('performanceSnapshot.signals.skills'),
          primary: `${signals.skills.evidenceInPeriod}`,
          denominator:
            signals.skills.lifetimeSkillCount > 0
              ? t('performanceSnapshot.signals.skillsDenominator', {
                  count: signals.skills.lifetimeSkillCount,
                })
              : null,
          measured: signals.skills.measured,
          noneInPeriod: false,
        },
      ]
    : [];

  /**
   * REFLECTIONS — a fifth signal, appended rather than woven in, and the `?` matters.
   *
   * The group was added to `signalsSummary` after CO-443 shipped, so a snapshot generated before
   * that has the other four groups and NOT this one. That row is not legacy — it must keep
   * rendering everything it does have. So an absent group drops the tile entirely; it does NOT
   * render as "Not enough signal", because that copy asserts something about the whole company
   * that was never actually measured for these rows.
   *
   * The headline is what the EMPLOYEE wrote. `withManagerResponse` is the denominator because it
   * is the only part of this signal that measures the manager, and a manager reading their own
   * report's snapshot should see the reply gap next to the reflections, not instead of them.
   */
  if (signals?.reflections) {
    tiles.push({
      key: 'reflections',
      label: t('performanceSnapshot.signals.reflections'),
      primary: `${signals.reflections.submitted}`,
      denominator: t('performanceSnapshot.signals.reflectionsDenominator', {
        count: signals.reflections.withManagerResponse,
      }),
      measured: signals.reflections.measured,
      // A plain COUNT has no denominator to be zero, so `0` is an honest measurement here too.
      noneInPeriod: false,
    });
  }

  return (
    <div className="mb-6 bg-card border border-border rounded-lg p-6" data-testid="snapshot-summary-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-lg font-semibold text-foreground">
          {t('performanceSnapshot.snapshotSummary')}
        </h3>
        <span className="text-sm text-muted-foreground">
          {t('performanceSnapshot.period')}:{' '}
          {/* 🔴 A RAW period token reverses under `ar` unless it is pinned LTR — a DOM assertion
              passed on exactly that bug last session. `dir` is applied ONLY to the raw token: a
              localized month name ("يوليو 2026") is real RTL text and pinning IT to LTR would be
              the same mistake in the other direction. */}
          <span
            dir={unreadablePeriod ? 'ltr' : undefined}
            className="tabular-nums"
            data-testid="snapshot-period-token"
          >
            {unreadablePeriod ? snapshot.period : monthLabel}
          </span>
        </span>
      </div>

      {/* CO-442 — the window line. It says what was counted and over what, in one sentence. */}
      {unreadablePeriod ? (
        /* 🔴 THE COUNTS LINE IS OMITTED ENTIRELY when the period cannot be read as a month. "…in
           month" would be a lie, and so would any other window word: a surviving pre-CO-439 row's
           numbers were computed over a ROLLING SIX MONTHS anchored to its generation date, not over
           the quarter on its label. The hint replaces it and claims only what is true — that the
           window cannot be named here.

           CO-513 generalised this from the legacy-quarter-specific copy it used to carry, with the
           legacy quarterly path itself. The `data-testid` keeps its historical name so the Wave 2
           lane-D assertions still address it; it is the unreadable-period hint now. */
        <div
          data-testid="snapshot-legacy-period-hint"
          className="mb-4 flex items-start gap-2 text-xs text-muted-foreground max-w-[70ch]"
        >
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            <span className="font-medium">{t('performanceSnapshot.periodUnreadable')}</span>{' '}
            {t('performanceSnapshot.periodUnreadableHint')}
          </span>
        </div>
      ) : (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="snapshot-window-line">
          <CalendarRange size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground">
            {/* Two sentences, not one with an optional clause: a snapshot generated before
                reflections were counted must not print ", 0 reflections in month" for a month
                nobody was ever asked about. The reflections wording appears only when the group
                is actually present on the row. */}
            {signals
              ? t(
                  signals.reflections
                    ? 'performanceSnapshot.window.countsWithReflections'
                    : 'performanceSnapshot.window.counts',
                  {
                    month: monthLabel,
                    goals: t('performanceSnapshot.window.goals', { count: signals.goals.total }),
                    checkIns: t('performanceSnapshot.window.checkIns', { count: signals.checkIns.completed }),
                    recognitions: t('performanceSnapshot.window.recognitions', {
                      count: signals.recognition.received,
                    }),
                    reflections: t('performanceSnapshot.window.reflections', {
                      count: signals.reflections?.submitted ?? 0,
                    }),
                  },
                )
              : monthLabel}
          </span>
          {inProgressNow && <InProgressChip />}
        </div>
      )}

      {inProgressNow && (
        <p className="mb-4 text-xs text-muted-foreground max-w-[70ch]">
          {t('performanceSnapshot.periodPicker.inProgressHint')}
        </p>
      )}

      {/* The month is over, but this snapshot was computed before it ended. Say what the numbers
          actually cover rather than either promising they will change or implying a whole month. */}
      {countedToPartialWindow && countedToLabel && (
        <p
          data-testid="snapshot-partial-window-note"
          className="mb-4 text-xs text-muted-foreground max-w-[70ch]"
        >
          {t('performanceSnapshot.periodPicker.countedTo', { date: countedToLabel })}
        </p>
      )}

      {signals ? (
        // Four tiles on a snapshot generated before reflections were counted, five after. A fixed
        // 4- or 5-column grid orphans one tile on the other shape, so the count drives the layout.
        <div className={`grid grid-cols-2 gap-4 ${tiles.length >= 5 ? 'md:grid-cols-3 xl:grid-cols-5' : 'md:grid-cols-4'}`}>
          {tiles.map((tile) => (
            <SignalTileCard key={tile.key} tile={tile} />
          ))}
        </div>
      ) : (
        /* LEGACY ROW — no `signalsSummary`. The retained composite is shown, labelled as what it
           is, so an old record still renders; it is not offered on anything written from CO-443
           onwards. The three source tiles keep their numbers but their captions no longer claim
           a window (see the header comment). */
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted rounded-lg p-4 text-center" data-testid="snapshot-legacy-composite">
              <div className="text-2xl font-semibold text-foreground tabular-nums">
                {snapshot.overallScore != null ? `${Math.round(snapshot.overallScore)}%` : '--'}
              </div>
              <div className="text-xs font-medium text-foreground mt-1">
                {t('performanceSnapshot.overallScore')}
              </div>
              <div className="text-xs text-muted-foreground mt-1 leading-snug">
                {t('performanceSnapshot.signals.legacyComposite')}
              </div>
            </div>
            {legacyGoals && (
              <div className="bg-muted rounded-lg p-4 text-center">
                <div className="text-2xl font-semibold text-foreground tabular-nums" dir="ltr">
                  {legacyGoals.completed ?? 0} / {legacyGoals.total ?? 0}
                </div>
                <div className="text-xs font-medium text-foreground mt-1">
                  {t('performanceSnapshot.signals.goals')}
                </div>
              </div>
            )}
            {legacyCheckIns && (
              <div className="bg-muted rounded-lg p-4 text-center">
                <div className="text-2xl font-semibold text-foreground tabular-nums">
                  {legacyCheckIns.total ?? 0}
                </div>
                <div className="text-xs font-medium text-foreground mt-1">
                  {t('performanceSnapshot.signals.checkIns')}
                </div>
              </div>
            )}
            {legacyRecognition && (
              <div className="bg-muted rounded-lg p-4 text-center">
                <div className="text-2xl font-semibold text-foreground tabular-nums">
                  {legacyRecognition.received ?? 0}
                </div>
                <div className="text-xs font-medium text-foreground mt-1">
                  {t('performanceSnapshot.signals.recognition')}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/** One labelled evidence list in the employee's read-only narrative. Renders nothing when empty. */
const EvidenceList = ({
  title,
  items,
  Icon,
  tone,
}: {
  title: string;
  items: unknown;
  Icon: typeof Star;
  tone: string;
}) => {
  const list = Array.isArray(items) ? items.filter((i): i is string => typeof i === 'string' && i.trim().length > 0) : [];
  if (list.length === 0) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
        <Icon size={18} className={tone} />
        <span>{title}</span>
      </h4>
      <ul className="space-y-2">
        {list.map((entry, index) => (
          <li key={index} className="flex items-start gap-2">
            <Icon size={16} className={`${tone} mt-0.5 flex-shrink-0`} />
            <span className="text-sm text-foreground">{entry}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const PerformanceSnapshot = () => {
  // `i18n` is read for the picker's direction and for month formatting (`Intl.DateTimeFormat`).
  const { t, i18n } = useTranslation();
  // Operators can switch individual tabs off per company from Module Config.
  const { isTabEnabled, loaded: tabConfigLoaded } = usePageTabConfig(PERFORMANCE_SNAPSHOT_TAB_MODULE_ID);
  /**
   * Whether the employee section is switched on for this company. Declared HERE, above the roster
   * fetch, because that fetch depends on it — computing it further down would put it in the temporal
   * dead zone for the effect's dependency array.
   */
  const employeeSectionEnabled = isTabEnabled(SECTION_EMPLOYEE);

  const myUserId = useSelector((state: { user: UserSliceShape }) => state.user.userId);
  const role = useSelector((state: { user: UserSliceShape }) => state.user.role);
  const isManager = useSelector((state: { user: UserSliceShape }) => state.user.isManager);
  const canAuthor = AUTHORING_ROLES.includes(role) || isManager;

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_EMPLOYEE);
  const [activeTab, setActiveTab] = useState('narrative');
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** Roster presentation + filters — see the block above `filteredEmployees`. */
  const [rosterView, setRosterView] = useState<'grid' | 'list'>('grid');
  const [rosterDept, setRosterDept] = useState('all');
  const [rosterManager, setRosterManager] = useState('all');
  const [rosterReviewed, setRosterReviewed] = useState<'all' | 'never' | 'reviewed'>('all');
  const [rosterPage, setRosterPage] = useState(0);
  const [rosterPageSize, setRosterPageSize] = useState(20);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [error, setError] = useState<PageError | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  /* ────────────────────────────────────────────────────────────────────────────────────────────
     AX2 / AX3 / AX4 — FOCUS MANAGEMENT AND ANNOUNCEMENT ACROSS THE MASTER↔DETAIL SWITCH.

     Picking a person UNMOUNTS the roster and mounting the detail view destroys the very button
     that was clicked, so `document.activeElement` fell back to `<body>`: a keyboard or
     screen-reader user lost their place on every selection, and again on every Back. Nothing here
     is cosmetic — with focus on `<body>` the next Tab restarts at the top of the document.

     The mechanism is deliberately ref-based rather than `autoFocus`: `autoFocus` fires on EVERY
     mount (including the remounts CO-441's history list causes) and would steal focus from a
     manager who is typing. These refs move focus only on the two transitions that destroy it.

     · `backToRosterRef`      — the detail view's first control; focus lands here on select (AX2).
     · `rosterCardRefs`       — id → the roster button for that person, so Back can put focus back
                                on the CARD THE USER CAME FROM rather than at the top (AX3).
     · `rosterHeadingRef`     — `tabIndex={-1}` fallback when that card is no longer rendered
                                (a filter or a page change happened while the detail view was up).
     · `pendingFocusTarget`   — set by the two handlers, consumed by the effect below AFTER the
                                new tree has committed. A ref, not state: it must not re-render.
     · `liveAnnouncement`     — the `role="status"` text (AX4). */
  const backToRosterRef = useRef<HTMLButtonElement | null>(null);
  const rosterCardRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const rosterHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const returnFocusEmployeeId = useRef<string | null>(null);
  const pendingFocusTarget = useRef<'detail' | 'roster' | null>(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  /**
   * CO-439 — the month Generate is addressed to, as a `yyyy-MM` token. The picker below is the
   * only writer. Defaults to the LAST COMPLETED month, matching the server's own default, so an
   * unattended Generate produces a whole month rather than a partial one.
   */
  const [selectedPeriod, setSelectedPeriod] = useState<string>(() => lastCompletedMonthPeriod());

  /**
   * CO-441 — the AUTHOR-side list of this employee's previous months. (`getEmployeeSnapshots` has
   * a second, SUBJECT-side caller above, for the employee's own period switcher; the two are
   * independent and neither may be folded into the other — this one is scoped to the picked
   * employee and includes Drafts, that one is the caller's own and excludes them.)
   */
  const [priorSnapshots, setPriorSnapshots] = useState<PerformanceSnapshotView[]>([]);
  const [isLoadingPrior, setIsLoadingPrior] = useState(false);

  /**
   * CO-441/CO-435 — the ONE snapshot the narrative panel may generate for WITHOUT being asked.
   *
   * An id, not a boolean, on purpose: it is checked against the snapshot actually on screen, so a
   * stale `true` left behind by an earlier open cannot arm the panel for a different month. `null`
   * means "nobody asked for a narrative here" and the panel offers its buttons instead of
   * spending a model call. See `openSnapshot`.
   */
  const [autoNarrativeSnapshotId, setAutoNarrativeSnapshotId] = useState<number | null>(null);

  // CO-437 employee mode — the caller's OWN snapshots.
  const [mySnapshots, setMySnapshots] = useState<PerformanceSnapshotView[]>([]);
  const [mySnapshotId, setMySnapshotId] = useState<number | null>(null);
  const [isLoadingMine, setIsLoadingMine] = useState(true);

  /**
   * CO-436 — the roster comes from the snapshot's own endpoint, which resolves title, department
   * and manager. `Recognition/Recipients` (the previous source) hardcoded all three to `''`, so the
   * search box below could never match on any of them. Scoping is server-side: Hr/Admin get the
   * company, a manager gets their direct reports.
   */
  useEffect(() => {
    if (!tabConfigLoaded) return;
    if (!employeeSectionEnabled || !canAuthor) {
      setIsLoadingEmployees(false);
      return;
    }
    const fetchEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const roster = await getSnapshotRoster();
        setEmployees(
          roster.map((r) => ({
            id: r.employeeId,
            name: r.fullName,
            position: r.title ?? '',
            department: r.departmentName ?? '',
            manager: r.managerName ?? '',
            lastReviewDate: r.lastSnapshotOn ?? '',
            lastReviewPeriod: r.lastSnapshotPeriod ?? '',
            nextReviewDue: '',
            imageUrl: r.imageUrl,
          })),
        );
      } catch {
        setError({ message: t('performanceSnapshot.loadError'), permanent: false });
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, [t, tabConfigLoaded, employeeSectionEnabled, canAuthor]);

  /**
   * CO-437 — employee mode. Drafts are filtered out client-side as well as server-side: a manager's
   * unfinished draft is not something its subject should be reading.
   *
   * CO-436 — this used to short-circuit on `canAuthor`, which meant an authoring caller NEVER
   * loaded their own snapshots. Everybody is the subject of their own snapshot, including Hr and
   * managers, and notification type 90 deep-links the subject here to acknowledge it. Fetching
   * unconditionally is what keeps that path alive when the roster comes back empty (see
   * `rosterIsEmpty` below).
   */
  useEffect(() => {
    if (!tabConfigLoaded) return;
    if (!employeeSectionEnabled || !myUserId) {
      setIsLoadingMine(false);
      return;
    }
    const fetchMine = async () => {
      setIsLoadingMine(true);
      try {
        const all = await getEmployeeSnapshots(myUserId);
        // CO-441 — `comparePeriodsDesc`, NEVER a raw string compare. A lexical sort put every
        // legacy `yyyy-Qn` row above every month of the same year, so `shared[0]` — the snapshot
        // this page opens on, and the one `myPendingAck` addresses Acknowledge to — was the legacy
        // quarter rather than the month the subject was notified about.
        const shared = all
          .filter((s) => normalizeSnapshotStatus(s.status) !== 'Draft')
          .sort((a, b) => comparePeriodsDesc(a.period, b.period));
        setMySnapshots(shared);
        setMySnapshotId(shared.length > 0 ? shared[0].id : null);
      } catch {
        setError({ message: t('performanceSnapshot.loadError'), permanent: false });
      } finally {
        setIsLoadingMine(false);
      }
    };
    fetchMine();
  }, [t, tabConfigLoaded, employeeSectionEnabled, myUserId]);

  /**
   * CO-441 — the AUTHOR's "previous months" list, and the first consumer of `getEmployeeSnapshots`
   * on this side of the page. Without it a manager had no way to see that a month already had a
   * snapshot, so the only route to an existing record was to guess its month and press Generate —
   * which, now that Generate is get-or-create, silently returned an old Submitted record with no
   * indication that it was not new.
   *
   * Unlike the subject-side fetch above this one KEEPS Drafts: an unfinished draft is precisely
   * what its author needs to find again.
   *
   * A failure here is deliberately quiet — the list renders its empty state. Raising the page
   * banner would overwrite a Generate refusal the manager is currently reading, and a missing
   * history list is not worth losing that sentence over.
   */
  useEffect(() => {
    if (!selectedEmployee) {
      setPriorSnapshots([]);
      return;
    }
    let cancelled = false;
    const fetchPrior = async () => {
      setIsLoadingPrior(true);
      try {
        const all = await getEmployeeSnapshots(selectedEmployee.id);
        // CO-441 — this comment used to claim a legacy `yyyy-Qn` token "sorts into the same year,
        // which is close enough". IT IS NOT: 'Q' collates after every digit, so every quarter row
        // sorted above every month of its year and the history list opened head-first on a legacy
        // record. `comparePeriodsDesc` orders both shapes semantically — see the API module.
        if (!cancelled) setPriorSnapshots([...all].sort((a, b) => comparePeriodsDesc(a.period, b.period)));
      } catch {
        if (!cancelled) setPriorSnapshots([]);
      } finally {
        if (!cancelled) setIsLoadingPrior(false);
      }
    };
    fetchPrior();
    return () => {
      cancelled = true;
    };
  }, [selectedEmployee]);

  /* ────────────────────────────────────────────────────────────────────────────────────────
     THE ROSTER, AS A REAL LIST.

     An HR user's roster is the WHOLE COMPANY, not a handful of direct reports, and the picker was
     a single unbounded grid behind one free-text box: every employee rendered at once, five to a
     row, with no way to narrow by department or manager and no way to scan a column of names.
     Filters + a list mode + paging, all client-side over the roster already in memory -- the
     endpoint returns the full list in one call, so paging here costs nothing and prevents the DOM
     from carrying a thousand avatar cards.
     ──────────────────────────────────────────────────────────────────────────────────────────── */

  const rosterDepartments = useMemo(
    () => Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort(),
    [employees],
  );
  const rosterManagers = useMemo(
    () => Array.from(new Set(employees.map((e) => e.manager).filter(Boolean))).sort(),
    [employees],
  );

  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return employees
      .filter((emp) => rosterDept === 'all' || emp.department === rosterDept)
      .filter((emp) => rosterManager === 'all' || emp.manager === rosterManager)
      // `lastReviewPeriod` is blank when the person has never had a snapshot -- the same signal the
      // card already renders, so the filter and the card can never disagree.
      .filter((emp) =>
        rosterReviewed === 'all'
        || (rosterReviewed === 'never' ? !emp.lastReviewPeriod : !!emp.lastReviewPeriod))
      .filter(
        (emp) =>
          !q
          || emp.name.toLowerCase().includes(q)
          || emp.department.toLowerCase().includes(q)
          || emp.position.toLowerCase().includes(q)
          || emp.manager.toLowerCase().includes(q),
      );
  }, [employees, searchQuery, rosterDept, rosterManager, rosterReviewed]);

  const rosterPageCount = Math.max(1, Math.ceil(filteredEmployees.length / rosterPageSize));
  // Narrowing the filters must not strand the reader on a page that no longer exists.
  useEffect(() => {
    setRosterPage(0);
  }, [searchQuery, rosterDept, rosterManager, rosterReviewed, rosterPageSize]);
  const rosterSafePage = Math.min(rosterPage, rosterPageCount - 1);
  const pagedEmployees = useMemo(
    () => filteredEmployees.slice(rosterSafePage * rosterPageSize, rosterSafePage * rosterPageSize + rosterPageSize),
    [filteredEmployees, rosterSafePage, rosterPageSize],
  );
  const rosterHasFilters =
    searchQuery.trim() !== '' || rosterDept !== 'all' || rosterManager !== 'all' || rosterReviewed !== 'all';
  const clearRosterFilters = () => {
    setSearchQuery('');
    setRosterDept('all');
    setRosterManager('all');
    setRosterReviewed('all');
  };

  /**
   * CO-439 — the picker's bounds and its `Date` view of `selectedPeriod`.
   *
   * `toMonth` is the CURRENT month, not the last completed one: the current month must be
   * SELECTABLE (marked in-progress), and only genuinely FUTURE months are out of reach. The
   * picker clamps a selection to this bound rather than disabling the option, so picking December
   * in August lands on August — which is the correct refusal, silently applied.
   *
   * Memoised because `YearMonthPicker` holds `date` in several effect dependency arrays; a fresh
   * `Date` object on every render would re-subscribe its keyboard and wheel listeners each time.
   */
  const pickerDate = useMemo(() => fromPeriod(selectedPeriod), [selectedPeriod]);
  const pickerMaxDate = useMemo(() => fromPeriod(currentMonthPeriod()), []);
  const isCurrentMonthSelected = selectedPeriod === currentMonthPeriod();

  const snapshotId = reviewData?.snapshotId ?? null;
  const snapshotStatus = reviewData?.snapshot ? normalizeSnapshotStatus(reviewData.snapshot.status) : null;
  const isDraft = snapshotStatus === 'Draft';

  /**
   * CO-435/CO-437 — Generate is get-or-create on the server, so it can hand back a snapshot that is
   * already Submitted or Acknowledged. `Update` refuses anything but a Draft, and autosave rightly
   * does not fire, which left every panel editable with nothing happening and no indicator at all —
   * the defect class CO-433 removed. Non-Draft means read-only, said once and enforced in the panels.
   */
  const isReadOnly = reviewData?.snapshot != null && !isDraft;

  /**
   * B1 — the open snapshot and the picked employee must AGREE before a panel renders.
   * `handleSelectEmployee` clears `reviewData` on every change of selection so they always do; this
   * is the assertion that keeps a wrong-employee write impossible even if a future edit adds
   * another way to move the selection.
   */
  const snapshotMatchesSelection = reviewData !== null && selectedEmployee?.id === reviewData.employeeId;

  /**
   * B1/CO-437 — Acknowledge belongs to the SUBJECT of a snapshot and to nobody else. CO-437 made
   * the server refuse it for anyone whose id is not `snapshot.EmployeeId`, so the authoring action
   * bar was offering a button that 403s on every manager-authored snapshot — the entire intended
   * use of this page. The one case where an author IS the subject (an Hr generating their own) is
   * kept; employee mode owns the button everywhere else, so the two modes never both claim it.
   */
  const isOwnSnapshot = reviewData?.snapshot != null && reviewData.snapshot.employeeId === myUserId;

  /**
   * CO-433 — the serialized payload the page WOULD write. Comparing this string against the last
   * one written is what keeps a panel that re-reports unchanged state (the narrative panel does, on
   * every mount) from triggering an endless autosave loop.
   */
  const pendingPayload = useMemo(
    () => (reviewData ? JSON.stringify(buildUpdateInput(reviewData)) : null),
    [reviewData],
  );
  const lastSavedPayload = useRef<string | null>(null);

  const persist = useCallback(
    async (payload: string, id: number) => {
      setSaveState('saving');
      try {
        const updated = await updateSnapshot(id, JSON.parse(payload) as SnapshotUpdateInput);
        lastSavedPayload.current = payload;
        setSaveState('saved');
        setReviewData((prev) => (prev && prev.snapshotId === id ? { ...prev, snapshot: updated } : prev));
        return updated;
      } catch {
        setSaveState('error');
        return null;
      }
    },
    [],
  );

  /** The scheduled debounce, held so an explicit flush can cancel it instead of double-writing. */
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingAutosave = useCallback(() => {
    if (autosaveTimer.current !== null) {
      clearTimeout(autosaveTimer.current);
      autosaveTimer.current = null;
    }
  }, []);

  /**
   * CO-433 — autosave. A no-op unless the snapshot is a Draft: `Update` rejects anything else, and
   * firing it against a Submitted snapshot would show a permanent "not saved" for a write the
   * server is right to refuse.
   */
  useEffect(() => {
    if (!snapshotId || !isDraft || pendingPayload === null) return;
    if (pendingPayload === lastSavedPayload.current) return;
    // Say a write is owed while the debounce counts down — see the `SaveState` comment.
    setSaveState('pending');
    autosaveTimer.current = setTimeout(() => {
      autosaveTimer.current = null;
      persist(pendingPayload, snapshotId);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => cancelPendingAutosave();
  }, [pendingPayload, snapshotId, isDraft, persist, cancelPendingAutosave]);

  /**
   * The write autosave still owes, mirrored into a ref so the unmount flush below can read it
   * without re-subscribing on every keystroke.
   */
  const pendingWrite = useRef<{ payload: string; id: number } | null>(null);
  useEffect(() => {
    pendingWrite.current =
      snapshotId && isDraft && pendingPayload !== null && pendingPayload !== lastSavedPayload.current
        ? { payload: pendingPayload, id: snapshotId }
        : null;
  }, [pendingPayload, snapshotId, isDraft]);

  /**
   * CO-433 — flush on the way out. The autosave cleanup's `clearTimeout` also runs on UNMOUNT, so
   * routing away inside the debounce window silently discarded the last 800 ms of typing while the
   * indicator still read nothing. The write goes out directly rather than through `persist`: this
   * component is being torn down, so there is no state left to update and nothing to await — the
   * XHR outlives the unmount.
   */
  useEffect(
    () => () => {
      const owed = pendingWrite.current;
      if (!owed) return;
      updateSnapshot(owed.id, JSON.parse(owed.payload) as SnapshotUpdateInput).catch(() => {
        /* the page is gone; there is nowhere left to report this */
      });
    },
    [],
  );

  /**
   * A full close or reload cannot be flushed the same way — the PUT carries an auth header, which
   * `sendBeacon` cannot set — so ask instead. The browser's own "leave site?" prompt is the only
   * honest signal available at that point.
   */
  useEffect(() => {
    const warnOnUnsaved = (event: BeforeUnloadEvent) => {
      if (!pendingWrite.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnOnUnsaved);
    return () => window.removeEventListener('beforeunload', warnOnUnsaved);
  }, []);

  /**
   * Writes whatever autosave still owes, cancelling the debounce so the same payload is not sent
   * twice. Returns `false` ONLY when a write was owed and it failed — the caller must then not
   * proceed with whatever would have discarded it or re-addressed it to another snapshot.
   */
  const flushPending = useCallback(async () => {
    cancelPendingAutosave();
    if (!snapshotId || !isDraft || pendingPayload === null) return true;
    if (pendingPayload === lastSavedPayload.current) return true;
    return (await persist(pendingPayload, snapshotId)) !== null;
  }, [cancelPendingAutosave, snapshotId, isDraft, pendingPayload, persist]);

  /**
   * B1 — changing the picked employee MUST clear the open snapshot.
   *
   * `reviewData` carries the snapshot id every write is addressed to, while the panels re-render
   * with the NEW employee's name the moment `selectedEmployee` changes. Leaving `reviewData` in
   * place therefore put employee B's name over employee A's snapshot, and — since CO-433 made
   * typing actually reach the server — the first keystroke on the Decisions tab autosaved B's
   * compensation input and risk flag into A's record. Clearing it is what makes that impossible.
   *
   * The ORDERING matters as much as the clear. A debounced write is already scheduled against A's
   * id, so it is flushed first: cancelling instead would silently drop up to 800 ms of the
   * manager's typing, and letting it land after the switch is the same misattribution by another
   * route. If the flush FAILS the selection does not move — A stays on screen with the failed
   * indicator and its Retry, so unsaved work is never discarded behind a person's back.
   */
  const handleSelectEmployee = async (employee: Employee): Promise<boolean> => {
    // Returns whether the selection now IS this employee - true when it already was, true when the
    // move succeeded, false ONLY when a failed flush vetoed it. `handleOpenEmployeeFromAnalysis`
    // depends on this: it must not switch sections after a veto, or it would land the manager in
    // the employee tab with the PREVIOUS person still loaded and their failed write behind them.
    if (selectedEmployee?.id === employee.id) return true;
    if (!(await flushPending())) return false;
    setReviewData(null);
    lastSavedPayload.current = null;
    setSaveState('idle');
    // Nobody has asked for a narrative for the next person yet. The id check below would already
    // refuse a stale arming, but leaving one lying around is how the next edit reintroduces it.
    setAutoNarrativeSnapshotId(null);
    setActiveTab('narrative');
    // AX2/AX3/AX4 — remember the card to come back to, arm the focus move, and say what happened.
    // Both are set BEFORE the state write so the effect below sees them on the resulting commit.
    returnFocusEmployeeId.current = employee.id;
    pendingFocusTarget.current = 'detail';
    setLiveAnnouncement(t('performanceSnapshot.a11y.snapshotFor', { name: employee.name }));
    setSelectedEmployee(employee);
    return true;
  };

  /**
   * Opens a snapshot the page already holds, without going back to the server.
   *
   * Shared by Generate and by click-to-load in the "previous months" list. The list already
   * carries the FULL `PerformanceSnapshotView`, so re-opening a month costs no request at all —
   * and, more importantly, no AI narrative: `Generate` is get-or-create and cheap, but the
   * narrative panel bills the model the first time it sees a snapshot with an empty
   * `narrativeSummary`, and the daily AI allowance is 3/day for a Manager.
   *
   * 🔴 `allowAutoNarrative` is what MAKES that second sentence true, and it is not optional.
   * CO-441's history list changes `reviewData.snapshotId`, which changes the wrapper `key` below,
   * which REMOUNTS `NarrativeCompilationPanel` — whose mount effect generates whenever the
   * snapshot carries no narrative and is still a Draft. Browsing four Draft months to find the
   * right one therefore billed the model four times and autosaved three narratives nobody asked
   * for into other months' records, exhausting a Manager's 3/day allowance before reaching the
   * month they wanted. That is the CO-435 defect this file documents having closed, arriving by a
   * new route. Only a DELIBERATE act — pressing Generate for the month on screen — may arm it;
   * every other way in renders the panel's own Generate / Write-it-myself buttons instead.
   */
  const openSnapshot = useCallback((
    employeeId: string,
    snapshot: PerformanceSnapshotView,
    allowAutoNarrative: boolean,
  ) => {
    const next: ReviewData = {
      employeeId,
      generatedAt: new Date().toISOString(),
      snapshotId: snapshot.id,
      snapshot,
      narrative: safeParse(snapshot.narrativeSummary),
      // CO-434: the VALIDATION column, not the skills summary.
      strengths: safeParse(snapshot.strengthsValidation),
      prompts: safeParse(snapshot.narrativePromptsSummary) as unknown as Record<string, string> | null,
      comparative: null,
      decisions: safeParse(snapshot.decisionsSummary),
    };
    // Baseline the autosave against what the server already holds, so merely opening a snapshot
    // never writes.
    lastSavedPayload.current = JSON.stringify(buildUpdateInput(next));
    setSaveState('idle');
    setAutoNarrativeSnapshotId(allowAutoNarrative ? snapshot.id : null);
    setReviewData(next);
    setActiveTab('narrative');
  }, []);

  /**
   * "View details" on an analysis row: leave the org-wide read and land in the employee section.
   *
   * INTERIM BY DESIGN, and the caller says so. `SnapshotsAnalysisPanel` is still a UI-only demo
   * over a generated fixture (see its header comment), so the id it hands over matches nothing on
   * the real roster - there is no org-wide coverage endpoint to make it real yet. Rather than
   * navigate to an employee that does not exist, this switches to the employee section and picks
   * a real one, which is what was asked for while the panel is a demo.
   *
   * WHEN THE ENDPOINT LANDS: the row id becomes a real `employeeId`, and the only change needed
   * here is to look it up in `employees` and fall back to the current behaviour when it is absent
   * (a person can be on the coverage report and off this manager's roster). The section switch,
   * the flush and the failure path below are already correct for that.
   */
  const handleOpenEmployeeFromAnalysis = async (employeeId: string) => {
    // The employee section can be switched off in Module Config, in which case the reconciliation
    // effect would immediately bounce us back - better to do nothing than to flicker.
    if (!employeeSectionEnabled) return;
    const target = employees.find((e) => e.id === employeeId) ?? employees[0];
    if (!target) return;
    // `handleSelectEmployee` flushes any owed autosave and refuses to move if that write fails,
    // so an unsaved edit is never dropped by a jump out of the analysis tab. Honour its answer:
    // switching sections anyway would show the employee tab loaded with the PREVIOUS person.
    if (!(await handleSelectEmployee(target))) return;
    setActiveSection(SECTION_EMPLOYEE);
  };

  /**
   * BACK, from one employee's snapshot to the roster.
   *
   * Same ordering rule as `handleSelectEmployee`, and for the same reason: a debounced write is
   * very likely still owed against THIS snapshot, so it is flushed FIRST. Cancelling instead would
   * silently drop up to 800 ms of the manager's typing, and letting it land after the view has
   * changed is the misattribution B1 exists to prevent. If the flush FAILS the view does not move
   * -- the manager stays on the person whose work has not been saved, looking at the failed
   * indicator and its Retry, rather than being returned to a roster with their edit gone.
   */
  const handleBackToRoster = async () => {
    if (!(await flushPending())) return;
    setReviewData(null);
    lastSavedPayload.current = null;
    setSaveState('idle');
    setAutoNarrativeSnapshotId(null);
    setActiveTab('narrative');
    // AX3/AX4 — hand focus back to the originating card and announce the list the user lands on.
    // `filteredEmployees` (not `employees`) is the count actually on screen, the same number the
    // roster's own result counter shows, so the announcement never contradicts the visible page.
    pendingFocusTarget.current = 'roster';
    setLiveAnnouncement(t('performanceSnapshot.a11y.employeeList', { total: filteredEmployees.length }));
    setSelectedEmployee(null);
  };

  /**
   * The consumer of `pendingFocusTarget`. Runs AFTER the commit that swapped roster↔detail, which
   * is the earliest point at which the ref for the newly mounted target exists.
   *
   * It is keyed on `selectedEmployee` and it CLEARS the flag first, so a re-render for any other
   * reason (an autosave tick, a tab switch) can never move focus a second time.
   */
  useEffect(() => {
    const target = pendingFocusTarget.current;
    if (!target) return;
    pendingFocusTarget.current = null;
    if (target === 'detail') {
      backToRosterRef.current?.focus();
      return;
    }
    const id = returnFocusEmployeeId.current;
    returnFocusEmployeeId.current = null;
    const card = id ? rosterCardRefs.current.get(id) ?? null : null;
    // The card can legitimately be gone (filters/pagination changed while the detail view was up);
    // the roster heading is `tabIndex={-1}` precisely so there is always somewhere sane to land.
    (card ?? rosterHeadingRef.current)?.focus();
  }, [selectedEmployee]);

  /** Registers/unregisters a roster button so Back can return focus to it. */
  const setRosterCardRef = useCallback((employeeId: string, el: HTMLButtonElement | null) => {
    if (el) rosterCardRefs.current.set(employeeId, el);
    else rosterCardRefs.current.delete(employeeId);
  }, []);

  const handleGenerateReview = async () => {
    if (!selectedEmployee) return;

    // Generate is get-or-create server-side, so clicking it again re-reads the SERVER copy over
    // whatever is on screen. Flush first — a re-open must never be a silent revert.
    if (!(await flushPending())) return;

    setIsGenerating(true);
    setError(null);
    try {
      /**
       * Generate is GET-OR-CREATE for ONE MONTH, so `snapshot` is just as likely to be an existing
       * record — already carrying decisions, prompts, a narrative and a Submitted or Acknowledged
       * status — as a fresh Draft. Everything below reads off the returned row rather than assuming
       * empty columns, `isDraft` drives whether the panels are editable, and the narrative panel
       * only calls the model when `narrativeSummary` really is empty. Nothing here is Draft-only.
       *
       * CO-439 — the month is explicit now. Omitting it would let the server pick, which is right
       * for an old SPA bundle and wrong for this one: the picker on screen would then be showing a
       * month the snapshot was not computed for.
       */
      const snapshot = await generateSnapshot(selectedEmployee.id, selectedPeriod);
      /**
       * OWNER DECISION 2026-08-24 - GENERATE NO LONGER CALLS THE MODEL. THE FLOW IS NOT AI-DEPENDENT.
       *
       * This was `openSnapshot(..., true)`, which armed `autoNarrativeSnapshotId` and made the
       * narrative panel fire an AI request the instant it mounted. So the very first thing that
       * happened after pressing Generate was a model call, and the whole snapshot - summary,
       * strengths, prompts, comparative, decisions, Submit - sat behind it. If the model was slow,
       * rate-limited (a Manager gets 3 generations a day) or simply down, the page opened on a
       * failure card and the manager's read of it was that the snapshot itself had failed.
       *
       * Nothing about the snapshot ever needed the narrative: Save, Submit and Acknowledge have
       * never inspected `reviewData.narrative`, and `buildUpdateInput` just omits the column when
       * it is null. The dependency was purely this arming flag. With `false`, Generate produces
       * the snapshot and the narrative tab shows its own Generate / Write-it-myself choice - the
       * AI is now an OPTION INSIDE the flow rather than a gate in front of it.
       *
       * `autoNarrativeSnapshotId` and the `allowAutoNarrative` parameter are deliberately KEPT
       * rather than deleted: they are the mechanism that stops the panel auto-billing on every
       * remount as the history list changes the snapshot id (CO-441), and that hazard is still
       * live. There is now simply no caller that passes `true`.
       */
      openSnapshot(selectedEmployee.id, snapshot, false);
      // Keep the "previous months" list truthful without a second round trip: a first Generate for
      // a month adds a row, a re-Generate replaces the one that is already there. CO-441 — RE-SORT
      // rather than prepend: the picker can address any past month, so a freshly generated row is
      // not necessarily the newest one and prepending it would put the list out of order.
      setPriorSnapshots((prev) =>
        [snapshot, ...prev.filter((s) => s.id !== snapshot.id && s.period !== snapshot.period)].sort(
          (a, b) => comparePeriodsDesc(a.period, b.period),
        ),
      );
    } catch (err) {
      /**
       * CO-436/CO-437/CO-439 — SURFACE THE SERVER'S OWN SENTENCE.
       *
       * The bare `catch` this replaces printed "Failed to generate snapshot. Please try again."
       * for every failure, including two 403s the server had explained precisely ("you cannot
       * author your own performance snapshot", "you can only work on snapshots for your own direct
       * reports") and the two CO-439 400s (a future month, a malformed period). "Please try again"
       * is not merely unhelpful there — it is false, because retrying can never succeed — and it
       * hid the separation-of-duties rule the refusal exists to teach.
       *
       * `getApiErrorMessage` is the house accessor for this (`utils/apiErrors.ts`): it reads the
       * `Result<T>.errors[].description` the server actually sends, prefers a situation-specific
       * description over `Error.cs` boilerplate, and falls back to our own translated string only
       * when the server said nothing usable. Note the fallback DIFFERS by class — a permanent
       * refusal must never be handed copy that invites a retry.
       */
      const permanent = isPermanentRefusal(err);
      setError({
        message: getApiErrorMessage(
          err,
          t,
          permanent ? t('performanceSnapshot.generateRefused') : t('performanceSnapshot.generateError'),
        ),
        permanent,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * CO-441 — click-to-load from the "previous months" list.
   *
   * A LEGACY `yyyy-Qn` row never reaches this handler (its row is not clickable), so the picker is
   * only ever moved to a real month and can never end up labelling a quarter's numbers as a month's.
   *
   * 🔴 READ-ONLY IN THE AI SENSE: `false` below. Opening a past month must never spend a model
   * call — see the `allowAutoNarrative` note on `openSnapshot`. A month that has no narrative
   * shows the panel's Generate button and waits to be asked.
   */
  const handleOpenPriorSnapshot = async (snapshot: PerformanceSnapshotView) => {
    if (!selectedEmployee || snapshot.employeeId !== selectedEmployee.id) return;
    if (reviewData?.snapshotId === snapshot.id) return;
    // Same ordering rule as `handleSelectEmployee`: flush first, and do not move if it fails.
    if (!(await flushPending())) return;
    setError(null);
    setSelectedPeriod(snapshot.period);
    openSnapshot(selectedEmployee.id, snapshot, false);
  };

  /** Explicit flush of whatever autosave has not written yet. */
  const handleSaveSnapshot = async () => {
    if (!snapshotId || pendingPayload === null) return;
    cancelPendingAutosave();
    await persist(pendingPayload, snapshotId);
  };

  const handleSubmitSnapshot = async () => {
    if (!snapshotId || pendingPayload === null) return;
    try {
      // Flush first, then hand over. A failed flush must NOT submit a snapshot that is missing the
      // manager's last edits.
      if (!(await flushPending())) return;
      const updated = await submitSnapshot(snapshotId);
      setReviewData((prev) => (prev ? { ...prev, snapshot: updated } : prev));
    } catch (err) {
      // Same class as Generate: a 403 or a state-machine 400 ("only a Draft may be submitted") is
      // permanent, and the server's own sentence beats our generic one.
      const permanent = isPermanentRefusal(err);
      setError({ message: getApiErrorMessage(err, t, t('performanceSnapshot.submitError')), permanent });
    }
  };

  const handleAcknowledgeSnapshot = async () => {
    if (!snapshotId) return;
    try {
      const updated = await acknowledgeSnapshot(snapshotId);
      setReviewData((prev) => (prev ? { ...prev, snapshot: updated } : prev));
    } catch (err) {
      const permanent = isPermanentRefusal(err);
      setError({ message: getApiErrorMessage(err, t, t('performanceSnapshot.acknowledgeError')), permanent });
    }
  };

  const handleAcknowledgeOwn = async (id: number) => {
    try {
      const updated = await acknowledgeSnapshot(id);
      setMySnapshots((prev) => prev.map((s) => (s.id === id ? updated : s)));
    } catch (err) {
      const permanent = isPermanentRefusal(err);
      setError({ message: getApiErrorMessage(err, t, t('performanceSnapshot.acknowledgeError')), permanent });
    }
  };

  // The two configurable sections. Either can be switched off per company by an Operator.
  // Snapshots Analysis is org-wide coverage, so it is an authoring-side section only.
  const sections = useMemo(
    () =>
      [
        { id: SECTION_EMPLOYEE, label: t('performanceSnapshot.sections.employeeSnapshot'), icon: FileText },
        { id: SECTION_ANALYSIS, label: t('performanceSnapshot.sections.snapshotsAnalysis'), icon: HeartPulse },
      ].filter((section) => isTabEnabled(section.id) && (canAuthor || section.id === SECTION_EMPLOYEE)),
    [t, isTabEnabled, canAuthor],
  );

  // The five INNER tabs of the employee section. These are deliberately NOT in Module Config —
  // the owner asked for exactly two children under Performance Snapshot, not seven.
  const innerTabs = [
    { id: 'narrative', label: t('performanceSnapshot.tabs.narrative'), icon: FileText },
    { id: 'strengths-skills', label: t('performanceSnapshot.tabs.strengthsSkills'), icon: Award },
    { id: 'prompts', label: t('performanceSnapshot.tabs.prompts'), icon: MessageSquare },
    { id: 'comparative', label: t('performanceSnapshot.tabs.comparative'), icon: BarChart3 },
    { id: 'decisions', label: t('performanceSnapshot.tabs.decisions'), icon: CheckCircle },
  ];

  const isAnalysisSection = activeSection === SECTION_ANALYSIS;

  // Only the employee section needs a generated snapshot; the analysis section is org-wide.
  const showInnerTabs = !isAnalysisSection && snapshotMatchesSelection;

  /**
   * B2/CO-436 — the ROSTER is the server's answer to "may this caller author?", and it is stricter
   * than `canAuthor`, which is client redux state (`role`, `isManager`). `AccountService`'s
   * `isManager` is `UserManagers.Any(ManagerId == me)`; the snapshot service additionally requires
   * the report to be non-archived, in the same company, and not the caller. So a manager whose
   * direct reports have all been OFFBOARDED still reads `isManager = true` here while `GET Roster`
   * correctly returns `[]` — and the authoring shell then handed them an empty picker, a disabled
   * Generate, no `mySnapshots` fetch, and therefore no route to acknowledge their own snapshot,
   * which notification type 90 deep-links them to.
   *
   * The controller, the service and `SnapshotRosterItemView` all document an empty roster as the
   * signal to fall back to employee mode. Honour it here rather than reproducing the server's
   * definition on the client: any client/server disagreement about who may author now degrades to
   * "read-only" instead of "locked out", which closes the whole class, not just this instance.
   */
  const rosterIsEmpty =
    canAuthor && tabConfigLoaded && employeeSectionEnabled && !isLoadingEmployees && employees.length === 0;
  const showEmployeeMode = !canAuthor || rosterIsEmpty;

  /**
   * B2, the general case. An author is ALSO the subject of their own snapshot, and notification
   * type 90 deep-links the subject to this page to acknowledge it. A manager's roster is their
   * reports, not themselves, so the authoring shell otherwise has no entry for the very snapshot
   * the link is about — the empty-roster fallback above only covers the caller who has no reports
   * at all. `mySnapshots` is fetched for every caller now, so the prompt is one lookup away.
   */
  const myPendingAck = mySnapshots.find((s) => normalizeSnapshotStatus(s.status) === 'Submitted') ?? null;

  // A section an operator switched off must not stay selected. If BOTH are off the page renders
  // its own empty state rather than a blank panel — see the render below.
  useEffect(() => {
    if (sections.length > 0 && !sections.some((section) => section.id === activeSection)) {
      setActiveSection(sections[0].id);
    }
  }, [sections, activeSection]);

  const header = (
    <div className="bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-purple-500/10 border-b border-border">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <FileText size={24} color="white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('performanceSnapshot.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {showEmployeeMode ? t('performanceSnapshot.employee.subtitle') : t('performanceSnapshot.subtitle')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  /**
   * CO-436/CO-437 — the banner, and the reason it is not one string.
   *
   * `data-permanent` is the discriminator a test asserts on, and `whitespace-pre-line` is
   * load-bearing: `getApiErrorMessage` joins MULTIPLE violations with a newline, because one
   * request can break several rules at once and showing them one at a time is a bad loop.
   *
   * A permanent refusal carries no retry wording and no retry affordance. The server's sentence
   * ("you can only work on performance snapshots for your own direct reports") is the whole
   * message — appending "please try again" to it would be actively false.
   */
  const errorBanner = error && (
    <div
      data-testid="snapshot-error-banner"
      data-permanent={error.permanent ? 'true' : 'false'}
      role="alert"
      className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
    >
      <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
      <span className="text-sm max-w-[70ch] whitespace-pre-line">{error.message}</span>
    </div>
  );

  /* ────────────────────────────────────────────────────────────────────────────────────────────
     CO-437 EMPLOYEE MODE — the page's second audience.
     The subject of a snapshot reads it and acknowledges it. No picker, no Generate, no Decisions
     panel (compensation input and risk flags are manager-only), no Save or Submit.

     B2 — this is ALSO where an authoring caller with an empty roster lands (`rosterIsEmpty`), with
     a banner saying why. Being unable to review anyone must never mean being unable to acknowledge
     your own snapshot.
     ──────────────────────────────────────────────────────────────────────────────────────────── */
  if (showEmployeeMode) {
    const mine = mySnapshots.find((s) => s.id === mySnapshotId) ?? null;
    const mineStatus = mine ? normalizeSnapshotStatus(mine.status) : null;
    const narrative = mine ? safeParse(mine.narrativeSummary) : null;
    const narrativeText = typeof narrative?.summary === 'string' ? (narrative.summary as string) : '';
    const prompts = (mine ? safeParse(mine.narrativePromptsSummary) : null) as unknown as Record<string, string> | null;
    const answered = prompts
      ? Object.entries(prompts).filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
      : [];

    return (
      <div className="min-h-screen bg-background">
        {header}
        <div className="max-w-[1600px] mx-auto px-6 py-8">
          {errorBanner}

          {/* B2 — say plainly why the authoring shell is not here, rather than letting an
              ex-manager conclude the page is broken. */}
          {rosterIsEmpty && (
            <div
              data-testid="snapshot-empty-roster-notice"
              className="mb-6 flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-900"
            >
              <Info size={20} className="flex-shrink-0 mt-0.5" />
              <p className="text-sm max-w-[70ch]">{t('performanceSnapshot.employee.emptyRosterNotice')}</p>
            </div>
          )}

          {!employeeSectionEnabled ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <p className="text-muted-foreground">{t('performanceSnapshot.allSectionsDisabled')}</p>
            </div>
          ) : isLoadingMine ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center text-muted-foreground">
              <Loader2 size={32} className="mx-auto mb-2 animate-spin opacity-50" />
              <p className="text-sm">{t('performanceSnapshot.employee.loading')}</p>
            </div>
          ) : !mine ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <FileText size={40} className="mx-auto mb-4 text-muted-foreground opacity-60" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t('performanceSnapshot.employee.emptyTitle')}
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {t('performanceSnapshot.employee.emptyDescription')}
              </p>
            </div>
          ) : (
            <>
              {/* Period switcher — only earns its place once there is more than one. */}
              {mySnapshots.length > 1 && (
                <div className="mb-6 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground me-1">
                    {t('performanceSnapshot.employee.periodLabel')}
                  </span>
                  {mySnapshots.map((s) => {
                    // CO-439 — a month renders as "July 2026"; a token with no month to name (a
                    // surviving pre-CO-439 `yyyy-Qn`, say) stays raw and stays pinned LTR, because
                    // a raw ASCII token reverses under `ar`.
                    const legacy = isRawPeriodToken(s.period);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setMySnapshotId(s.id)}
                        aria-pressed={s.id === mySnapshotId}
                        dir={legacy ? 'ltr' : undefined}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium tabular-nums transition-colors duration-200 ${
                          s.id === mySnapshotId
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                      >
                        {legacy ? s.period : formatPeriodLabel(s.period, i18n.language)}
                      </button>
                    );
                  })}
                </div>
              )}

              <SnapshotSummaryCard snapshot={mine} />

              <div
                className="mb-6 flex flex-wrap items-center justify-between gap-3 bg-card border border-border rounded-lg p-4"
                data-testid="snapshot-employee-statusbar"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    {t('performanceSnapshot.status')}:
                  </span>
                  <span
                    data-testid="snapshot-status-badge"
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[mineStatus ?? 'Draft']}`}
                  >
                    {t(STATUS_LABEL_KEY[mineStatus ?? 'Draft'])}
                  </span>
                </div>
                {mineStatus === 'Submitted' ? (
                  <button
                    type="button"
                    onClick={() => handleAcknowledgeOwn(mine.id)}
                    data-testid="snapshot-acknowledge"
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                  >
                    <ThumbsUp size={16} />
                    <span>{t('performanceSnapshot.acknowledge')}</span>
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t('performanceSnapshot.employee.acknowledgedNote')}
                  </span>
                )}
              </div>

              <div className="space-y-6">
                {narrativeText.trim().length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-foreground flex items-center gap-2 mb-4">
                      <FileText size={22} />
                      <span>{t('performanceSnapshot.narrative.title')}</span>
                    </h3>
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap max-w-[70ch]">
                      {narrativeText}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <EvidenceList
                    title={t('performanceSnapshot.narrative.goalsAchieved')}
                    items={narrative?.goalsAchieved}
                    Icon={CheckCircle2}
                    tone="text-emerald-600"
                  />
                  <EvidenceList
                    title={t('performanceSnapshot.narrative.impactHighlights')}
                    items={narrative?.impactHighlights}
                    Icon={Star}
                    tone="text-pink-500"
                  />
                  <EvidenceList
                    title={t('performanceSnapshot.narrative.feedbackThemes')}
                    items={narrative?.feedbackThemes}
                    Icon={MessageCircle}
                    tone="text-blue-500"
                  />
                  <EvidenceList
                    title={t('performanceSnapshot.narrative.behaviorPatterns')}
                    items={narrative?.behaviorPatterns}
                    Icon={Activity}
                    tone="text-purple-500"
                  />
                  <EvidenceList
                    title={t('performanceSnapshot.narrative.goalsMissed')}
                    items={narrative?.goalsMissed}
                    Icon={XCircle}
                    tone="text-red-500"
                  />
                </div>

                {answered.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-foreground flex items-center gap-2 mb-4">
                      <MessageSquare size={22} />
                      <span>{t('performanceSnapshot.employee.managerNotes')}</span>
                    </h3>
                    <dl className="space-y-5">
                      {answered.map(([id, value]) => (
                        <div key={id}>
                          <dt className="text-sm font-medium text-muted-foreground mb-1">
                            {NARRATIVE_PROMPT_QUESTION_KEYS[id]
                              ? t(NARRATIVE_PROMPT_QUESTION_KEYS[id])
                              : id}
                          </dt>
                          <dd className="text-sm text-foreground leading-relaxed whitespace-pre-wrap max-w-[70ch]">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────────────────────────────────────────────────────────
     AUTHORING MODE — Hr / Admin / people-manager.
     ──────────────────────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-background">
      {header}

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {errorBanner}

        {/* B2 — the author's own snapshot, waiting on them. See `myPendingAck`. Employee mode has
            its own Acknowledge and returns before this point, so only one of the two ever renders. */}
        {myPendingAck && (
          <div
            data-testid="snapshot-own-pending-ack"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg"
          >
            <div className="flex items-start gap-3">
              <ThumbsUp size={20} className="text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-900 max-w-[70ch]">
                  {t('performanceSnapshot.ownPendingAcknowledgement')}
                </p>
                <span
                  className="text-xs text-emerald-800 tabular-nums"
                  dir={isRawPeriodToken(myPendingAck.period) ? 'ltr' : undefined}
                >
                  {isRawPeriodToken(myPendingAck.period)
                    ? myPendingAck.period
                    : formatPeriodLabel(myPendingAck.period, i18n.language)}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleAcknowledgeOwn(myPendingAck.id)}
              data-testid="snapshot-acknowledge-own"
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
            >
              <ThumbsUp size={16} />
              <span>{t('performanceSnapshot.acknowledge')}</span>
            </button>
          </div>
        )}

        {/* Section tabs — the two configurable children of this page (Module Config, Operator). */}
        {sections.length > 1 && (
          <div className="mb-6 bg-card border border-border rounded-lg p-2">
            <div className="flex gap-2">
              {sections.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    data-testid={`snapshot-section-${section.id}`}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                      activeSection === section.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    <SectionIcon size={16} />
                    <span>{section.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Both sections switched off by the Operator — say so instead of rendering nothing. */}
        {sections.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <p className="text-muted-foreground">{t('performanceSnapshot.allSectionsDisabled')}</p>
          </div>
        )}

        {/* The employee picker, snapshot summary and action bar belong to the employee section
            only — the analysis section is org-wide and needs no selected employee. */}
        {employeeSectionEnabled && !isAnalysisSection && (
        <>

        {/* AX4 — the master↔detail change was previously SILENT to a screen reader: the only
            `aria-live` on the page was the autosave indicator, which renders nothing while idle.
            This region is mounted for the whole employee section (never conditionally, or the
            insertion races its own content) and only its TEXT changes on select / Back.
            🔴 The `relative` wrapper is not decoration: Tailwind's `sr-only` is
            `position:absolute`, and with no positioned ancestor it escapes the main scroll
            container's clip and stretches the document scroll area (the phantom-scrollbar
            class of bug). */}
        <div className="relative">
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
            data-testid="snapshot-live-region"
          >
            {liveAnnouncement}
          </div>
        </div>

        {/* ────────────────────────────────────────────────────────────────────────────────
            MASTER-DETAIL. Owner decision 2026-08-24.

            The page used to stack everything: the roster stayed on screen above the month picker,
            the snapshot summary, the status bar and the five tabs, so a manager reviewing one
            person scrolled past twenty faces to reach the thing they were working on, and the
            answer to "whose snapshot is this?" was a single highlighted card far above the fold.

            Now picking a person REPLACES the roster with their snapshot, headed by their name and
            face, with one way back. Only one of these two blocks is ever mounted.
            ──────────────────────────────────────────────────────────────────────────────────── */}

        {/* MASTER — the roster. Only while nobody is picked.

            🔴 A ROSTER CARD HAS NO SELECTED STATE, and must not grow one back. It used to carry
            `aria-pressed`, a primary border and a checkmark for the picked employee — all of which
            became UNREACHABLE the moment selecting someone started unmounting this whole block:
            the "selected" render is never committed, so that styling could only ever be dead code
            and the ARIA state could only ever be `false`. These are navigation buttons now, and
            they are addressed by `data-testid="snapshot-roster-card"` rather than by a toggle
            state they do not have. */}
        {!selectedEmployee && (
        <div className="mb-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              {/* AX3 — `tabIndex={-1}` makes this programmatically focusable (but NOT a tab stop)
                  so Back always has a landing place when the originating card is gone. */}
              <h3
                ref={rosterHeadingRef}
                tabIndex={-1}
                className="text-lg font-semibold text-foreground flex items-center gap-2 outline-none"
              >
                <Users size={20} />
                <span>{t('performanceSnapshot.selectEmployee')}</span>
              </h3>
              {/* Grid reads as faces, list reads as a column of names. Which one is right depends
                  on whether you are recognising someone or scanning for them, so it is the
                  reader's call, not ours. */}
              <div className="flex items-center gap-1 p-1 rounded-lg border border-border" role="group">
                {([
                  { id: 'grid' as const, icon: LayoutGrid, label: t('performanceSnapshot.roster.gridView') },
                  { id: 'list' as const, icon: List, label: t('performanceSnapshot.roster.listView') },
                ]).map((mode) => {
                  const ModeIcon = mode.icon;
                  const active = rosterView === mode.id;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setRosterView(mode.id)}
                      aria-pressed={active}
                      aria-label={mode.label}
                      title={mode.label}
                      data-testid={`roster-view-${mode.id}`}
                      className={`p-2 rounded-md transition-colors duration-200 ${
                        active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <ModeIcon size={16} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search */}
            <div className="mb-4 relative">
              <Search
                size={18}
                className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder={t('performanceSnapshot.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full ps-10 pe-4 py-2.5 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>

            {/* Narrowing, for a roster that is the whole company. Every option is derived from the
                roster actually loaded, so a filter can never offer a value that matches nobody. */}
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <select
                value={rosterDept}
                onChange={(e) => setRosterDept(e.target.value)}
                aria-label={t('performanceSnapshot.roster.departmentFilter')}
                data-testid="roster-filter-department"
                className="py-2 px-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">{t('performanceSnapshot.roster.allDepartments')}</option>
                {rosterDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select
                value={rosterManager}
                onChange={(e) => setRosterManager(e.target.value)}
                aria-label={t('performanceSnapshot.roster.managerFilter')}
                data-testid="roster-filter-manager"
                className="py-2 px-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">{t('performanceSnapshot.roster.allManagers')}</option>
                {rosterManagers.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                value={rosterReviewed}
                onChange={(e) => setRosterReviewed(e.target.value as 'all' | 'never' | 'reviewed')}
                aria-label={t('performanceSnapshot.roster.reviewFilter')}
                data-testid="roster-filter-review"
                className="py-2 px-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="all">{t('performanceSnapshot.roster.review.all')}</option>
                <option value="never">{t('performanceSnapshot.roster.review.never')}</option>
                <option value="reviewed">{t('performanceSnapshot.roster.review.reviewed')}</option>
              </select>
              {rosterHasFilters && (
                <button
                  type="button"
                  onClick={clearRosterFilters}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                >
                  {t('performanceSnapshot.roster.clearFilters')}
                </button>
              )}
              <span className="ms-auto text-sm text-muted-foreground tabular-nums" data-testid="roster-result-count">
                {t('performanceSnapshot.roster.matchCount', { count: filteredEmployees.length })}
              </span>
            </div>

            {/* Employee Grid — ONE PAGE of it. `pagedEmployees`, never `filteredEmployees`. */}
            {isLoadingEmployees ? (
              <div className="text-center py-8 text-muted-foreground">
                <Loader2 size={32} className="mx-auto mb-2 animate-spin opacity-50" />
                <p className="text-sm">{t('performanceSnapshot.loadingEmployees')}</p>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <UserX size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('performanceSnapshot.noEmployeesFound')}</p>
              </div>
            ) : rosterView === 'list' ? (
              /* LIST — one row per person, so names read as a column instead of a mosaic. Same
                 fields as the card, laid out left to right; the avatar shrinks because in list
                 mode the name is doing the identifying, not the face. */
              <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
                {pagedEmployees.map((employee) => (
                  <li key={employee.id}>
                    <button
                      type="button"
                      ref={(el) => setRosterCardRef(employee.id, el)}
                      onClick={() => void handleSelectEmployee(employee)}
                      data-testid="snapshot-roster-card"
                      className="w-full flex items-center gap-3 px-4 py-3 text-start hover:bg-accent transition-colors duration-200"
                    >
                      {employee.imageUrl ? (
                        <img src={employee.imageUrl} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0" aria-hidden="true">
                          {employee.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-foreground truncate">{employee.name}</span>
                        {employee.position && (
                          <span className="block text-xs text-muted-foreground truncate">{employee.position}</span>
                        )}
                      </span>
                      <span className="hidden md:block w-40 shrink-0 text-sm text-muted-foreground truncate">
                        {employee.department}
                      </span>
                      <span className="hidden lg:block w-40 shrink-0 text-sm text-muted-foreground truncate">
                        {employee.manager}
                      </span>
                      <span className="w-32 shrink-0 text-end text-xs text-muted-foreground tabular-nums">
                        {employee.lastReviewPeriod ? (
                          <span dir={isRawPeriodToken(employee.lastReviewPeriod) ? 'ltr' : undefined}>
                            {isRawPeriodToken(employee.lastReviewPeriod)
                              ? employee.lastReviewPeriod
                              : formatPeriodLabel(employee.lastReviewPeriod, i18n.language)}
                          </span>
                        ) : (
                          t('performanceSnapshot.roster.review.never')
                        )}
                      </span>
                      <ChevronRight size={16} className="shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {pagedEmployees.map((employee) => (
                  <button
                    key={employee.id}
                    // B1 — NOT a bare `setSelectedEmployee`: the handler flushes and clears the
                    // open snapshot first, so a write can never be attributed to the wrong person.
                    ref={(el) => setRosterCardRef(employee.id, el)}
                    onClick={() => void handleSelectEmployee(employee)}
                    data-testid="snapshot-roster-card"
                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-primary hover:bg-accent transition-all duration-200"
                  >
                    {employee.imageUrl ? (
                      <img
                        src={employee.imageUrl}
                        alt={employee.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="text-center">
                      <div className="font-semibold text-foreground text-sm">{employee.name}</div>
                      {employee.position && (
                        <div className="text-xs text-muted-foreground">{employee.position}</div>
                      )}
                      {employee.department && (
                        <div className="text-xs text-muted-foreground">{employee.department}</div>
                      )}
                      {/* CO-436 — what the picker was missing: when this person was last reviewed.
                          CO-439 — as a month name, not as a raw token. `dir` is pinned only for a
                          raw token, which is what reverses under `ar`. */}
                      {employee.lastReviewPeriod && (
                        <div
                          className="text-xs text-muted-foreground mt-1 tabular-nums"
                          dir={isRawPeriodToken(employee.lastReviewPeriod) ? 'ltr' : undefined}
                        >
                          {t('performanceSnapshot.lastSnapshotPeriod', {
                            period: isRawPeriodToken(employee.lastReviewPeriod)
                              ? employee.lastReviewPeriod
                              : formatPeriodLabel(employee.lastReviewPeriod, i18n.language),
                          })}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Paging. Only earns its place once there is more than one page. */}
            {!isLoadingEmployees && rosterPageCount > 1 && (
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t('performanceSnapshot.roster.rowsPerPage')}</span>
                  <select
                    value={rosterPageSize}
                    onChange={(e) => setRosterPageSize(Number(e.target.value))}
                    className="py-2 px-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  >
                    {[10, 20, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                  </select>
                </label>
                <span className="ms-auto text-sm text-muted-foreground tabular-nums">
                  {t('performanceSnapshot.roster.pageOf', { page: rosterSafePage + 1, pages: rosterPageCount })}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setRosterPage((prev) => Math.max(0, prev - 1))}
                    disabled={rosterSafePage === 0}
                    aria-label={t('performanceSnapshot.roster.previousPage')}
                    className="p-2 rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <ChevronLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setRosterPage((prev) => Math.min(rosterPageCount - 1, prev + 1))}
                    disabled={rosterSafePage >= rosterPageCount - 1}
                    aria-label={t('performanceSnapshot.roster.nextPage')}
                    className="p-2 rounded-md border border-border text-foreground hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <ChevronRight size={16} className="rtl:rotate-180" aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

        {/* DETAIL — one person's snapshot. Only once they are picked. */}
        {selectedEmployee && (
        <>
        {/* Who this is, and the one way back. The subject of the record is named at the top of it,
            not inferred from a highlighted card somewhere above the fold. */}
        <div
          className="mb-6 bg-card border border-border rounded-lg p-4 flex flex-wrap items-center gap-4"
          data-testid="snapshot-employee-header"
        >
          {/* AX2 — the focus target on select: the first control of the view the user just
              entered, and the one that undoes the transition. */}
          <button
            type="button"
            ref={backToRosterRef}
            onClick={() => void handleBackToRoster()}
            data-testid="snapshot-back-to-roster"
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors duration-200"
          >
            {/* The arrow is a DIRECTION, so it mirrors under `ar` where "back" is to the right. */}
            <ArrowLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
            <span>{t('performanceSnapshot.backToRoster')}</span>
          </button>
          <div className="w-px self-stretch bg-border" aria-hidden="true" />
          {selectedEmployee.imageUrl ? (
            <img
              src={selectedEmployee.imageUrl}
              alt=""
              className="w-11 h-11 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0" aria-hidden="true">
              {selectedEmployee.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-foreground truncate" data-testid="snapshot-employee-name">
              {selectedEmployee.name}
            </div>
            {(selectedEmployee.position || selectedEmployee.department) && (
              <div className="text-xs text-muted-foreground truncate">
                {[selectedEmployee.position, selectedEmployee.department].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-card border border-border rounded-lg p-6">
            {/* --------------------------------------------------------------------------------
                THE GENERATE ACTION, revealed only once an employee is picked — which, since the
                master-detail split, is the only state this block renders in at all.

                It used to sit in the roster card's header, permanently visible and permanently
                disabled until a selection existed, which read as a page-level action ("generate
                snapshots") rather than what it is ("generate THIS person's snapshot for THIS
                month"). There is no disabled affordance left to explain.

                CO-439 — the month picker travels WITH the button because the two are one action:
                the snapshot is for a month, and until this existed the month was whatever quarter
                the server happened to compute on the day.
                -------------------------------------------------------------------------------- */}
            {selectedEmployee && (
              <div
                className="flex flex-wrap items-center gap-3"
                data-testid="snapshot-generate-bar"
              >
                <div className="flex flex-wrap items-center gap-2" data-testid="snapshot-period-picker">
                  <span className="text-sm text-muted-foreground">
                    {t('performanceSnapshot.periodPicker.label')}
                  </span>
                  <YearMonthPicker
                    date={pickerDate}
                    onChange={(d) => setSelectedPeriod(toPeriod(d))}
                    toMonth={pickerMaxDate}
                    isRtl={i18n.dir() === 'rtl'}
                    compact
                  />
                  {isCurrentMonthSelected && <InProgressChip />}
                </div>
                <button
                  onClick={handleGenerateReview}
                  disabled={isGenerating}
                  data-testid="snapshot-generate"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>{t('performanceSnapshot.generating')}</span>
                    </>
                  ) : (
                    <>
                      {/* 🔴 A PLAY GLYPH, NOT SPARKLES. This button computes the month's snapshot
                          from records that already exist; it has NEVER called a model, and since
                          the 2026-08-24 change it does not even arm one (measured on dev: zero
                          /api/AI/Chat requests across Generate → Save → Submit). A sparkle is the
                          house sign for "this spends AI", so wearing one here promised a cost the
                          action does not incur and implied a dependency the flow does not have. */}
                      <Play size={18} />
                      <span>{t('performanceSnapshot.generate')}</span>
                    </>
                  )}
                </button>
                {/* Said BEFORE the snapshot exists, not only after it: a manager choosing the
                    current month deliberately should know the numbers are partial before they
                    read them. */}
                {isCurrentMonthSelected && (
                  <p
                    data-testid="snapshot-in-progress-hint"
                    className="basis-full text-xs text-muted-foreground max-w-[70ch]"
                  >
                    {t('performanceSnapshot.periodPicker.inProgressHint')}
                  </p>
                )}
              </div>
            )}

            {/* CO-441 — PREVIOUS MONTHS. Only earns its place once someone is picked. */}
            {selectedEmployee && (
              <div className="mt-6 pt-4 border-t border-border" data-testid="snapshot-prior-months">
                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
                  <History size={16} />
                  <span>{t('performanceSnapshot.priorMonths.title')}</span>
                </h4>
                {isLoadingPrior ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" />
                    <span>{t('performanceSnapshot.priorMonths.loading')}</span>
                  </div>
                ) : priorSnapshots.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="snapshot-prior-months-empty">
                    {t('performanceSnapshot.priorMonths.empty')}
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {priorSnapshots.map((snapshot) => {
                      const status = normalizeSnapshotStatus(snapshot.status);
                      const legacy = isRawPeriodToken(snapshot.period);
                      const label = legacy
                        ? snapshot.period
                        : formatPeriodLabel(snapshot.period, i18n.language);
                      const isOpen = reviewData?.snapshotId === snapshot.id;

                      /**
                       * 🔴 A ROW WHOSE PERIOD IS NOT A MONTH IS NOT CLICKABLE, and that is
                       * deliberate, not an oversight. A surviving pre-CO-439 row's numbers were
                       * computed over a ROLLING SIX MONTHS anchored to its generation date — not
                       * over the quarter on its label — so loading it would move the month picker
                       * to a month it was never computed for and present a six-month total as that
                       * month's. It is listed, labelled for what it is, and left alone.
                       *
                       * CO-513 generalised the predicate and the copy from the legacy-quarter
                       * specifics they carried; the `data-testid` keeps its historical name so the
                       * Wave 2 lane-D assertions still address it.
                       */
                      if (legacy) {
                        return (
                          <li
                            key={snapshot.id}
                            data-testid="snapshot-prior-month-legacy"
                            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border bg-card text-muted-foreground"
                          >
                            <span dir="ltr" className="text-sm tabular-nums">
                              {label}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[status]}`}>
                              {t(STATUS_LABEL_KEY[status])}
                            </span>
                            <span className="text-xs">{t('performanceSnapshot.periodUnreadable')}</span>
                          </li>
                        );
                      }

                      return (
                        <li key={snapshot.id}>
                          <button
                            type="button"
                            onClick={() => void handleOpenPriorSnapshot(snapshot)}
                            aria-pressed={isOpen}
                            data-testid="snapshot-prior-month"
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors duration-200 ${
                              isOpen
                                ? 'border-primary bg-accent'
                                : 'border-border hover:border-primary hover:bg-accent'
                            }`}
                          >
                            <span className="text-sm text-foreground">{label}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[status]}`}>
                              {t(STATUS_LABEL_KEY[status])}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        </>
        )}

        {/* Snapshot Summary Card */}
        {reviewData?.snapshot && snapshotMatchesSelection && <SnapshotSummaryCard snapshot={reviewData.snapshot} />}

        {/* Status Bar — status, the page's single save indicator, and the workflow actions. */}
        {reviewData?.snapshot && snapshotStatus && snapshotMatchesSelection && (
          <div
            className="mb-6 flex flex-wrap items-center justify-between gap-3 bg-card border border-border rounded-lg p-4"
            data-testid="snapshot-statusbar"
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">{t('performanceSnapshot.status')}:</span>
              <span
                data-testid="snapshot-status-badge"
                className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[snapshotStatus]}`}
              >
                {t(STATUS_LABEL_KEY[snapshotStatus])}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SaveIndicator state={saveState} onRetry={handleSaveSnapshot} />
              {snapshotStatus === 'Draft' && (
                <>
                  <button
                    onClick={handleSaveSnapshot}
                    data-testid="snapshot-save-draft"
                    className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-accent transition-colors"
                  >
                    <Save size={16} />
                    <span>{t('performanceSnapshot.saveDraft')}</span>
                  </button>
                  <button
                    onClick={handleSubmitSnapshot}
                    data-testid="snapshot-submit"
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors"
                  >
                    <Send size={16} />
                    <span>{t('performanceSnapshot.submit')}</span>
                  </button>
                </>
              )}
              {/* B1/CO-437 — the subject acknowledges, nobody else. See `isOwnSnapshot`. */}
              {snapshotStatus === 'Submitted' &&
                (isOwnSnapshot ? (
                  <button
                    onClick={handleAcknowledgeSnapshot}
                    data-testid="snapshot-acknowledge"
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                  >
                    <ThumbsUp size={16} />
                    <span>{t('performanceSnapshot.acknowledge')}</span>
                  </button>
                ) : (
                  <span
                    data-testid="snapshot-awaiting-acknowledgement"
                    className="text-xs text-muted-foreground"
                  >
                    {t('performanceSnapshot.awaitingAcknowledgement', {
                      name: reviewData.snapshot.employeeName || selectedEmployee?.name || '',
                    })}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* A snapshot that is no longer a Draft cannot be written to. Say so once, here, rather
            than letting every panel accept typing that goes nowhere. */}
        {isReadOnly && snapshotMatchesSelection && (
          <div
            data-testid="snapshot-readonly-notice"
            className="mb-6 flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-900"
          >
            <Info size={20} className="flex-shrink-0 mt-0.5" />
            <p className="text-sm max-w-[70ch]">
              {snapshotStatus === 'Acknowledged'
                ? t('performanceSnapshot.readOnlyAcknowledged')
                : t('performanceSnapshot.readOnlySubmitted')}
            </p>
          </div>
        )}

        </>
        )}

        {/* Content Area */}
        <div className="space-y-6">
          {/* Tab Navigation */}
          {showInnerTabs && (
            <div className="bg-card border border-border rounded-lg p-2">
              <div className="flex gap-2">
                {innerTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      data-testid={`snapshot-tab-${tab.id}`}
                      className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        {isAnalysisSection ? (
          <SnapshotsAnalysisPanel onOpenEmployeeSnapshot={(id) => void handleOpenEmployeeFromAnalysis(id)} />
        ) : !employeeSectionEnabled ? null : !selectedEmployee ? (
          /* MASTER VIEW — the roster IS the content. The "no snapshot yet" card below explains
             what a snapshot contains, which is an answer to a question nobody has asked until
             they have chosen whose snapshot they mean. */
          null
        ) : !reviewData || !snapshotMatchesSelection ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={40} className="text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {t('performanceSnapshot.emptyState.title')}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t('performanceSnapshot.emptyState.description')}
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mt-8">
              <div className="bg-muted rounded-lg p-4">
                <Target size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.goalsAnalysis')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.goalsAnalysisDesc')}
                </p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <Heart size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.impactHighlights')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.impactHighlightsDesc')}
                </p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <MessageSquare size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.feedbackThemes')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.feedbackThemesDesc')}
                </p>
              </div>
              <div className="bg-muted rounded-lg p-4">
                <TrendingUp size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.behaviorPatterns')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.behaviorPatternsDesc')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* CO-432 — the key is the SNAPSHOT, not the tab.
             It used to be `key={activeTab}`, which remounted the active panel on every tab switch
             and threw away every unsaved edit the manager had typed.

             🔴 CORRECTION (CO-435, measured on dev @ 4df8d48f). This comment used to claim the key
             "keeps panel state alive across tab switches". IT DOES NOT, and believing it cost a
             defect. What survives a tab switch is the state this PAGE holds (`reviewData`), which
             each panel re-seeds from on mount. Panel-LOCAL state does not survive, because the
             panels below are CONDITIONALLY RENDERED on `activeTab`: leaving a tab unmounts its
             panel and returning remounts it, and no `key` can prevent that. The key only decides
             whether a panel that is rendered is the SAME instance as before.

             B1 — the key carries the EMPLOYEE id too. `snapshotId` alone did NOT change when the
             manager picked a different person (the picker only moved `selectedEmployee`), so
             employee A's panels stayed mounted, re-rendered under B's name, and the first keystroke
             autosaved B's answers into A's snapshot. The selection handler clears `reviewData`
             now, so the pair always agrees; keying on both means a stale panel cannot survive even
             if a future edit re-seeds `reviewData` without clearing first. */
          <div
            key={`${reviewData.employeeId}:${reviewData.snapshotId ?? 'no-snapshot'}`}
            className="transition-all duration-300 ease-in-out"
          >
            {/*
              🔴 CO-435 — THE NARRATIVE PANEL IS RENDERED AND HIDDEN, NEVER UNMOUNTED. A FAILED
              GENERATION WAS RE-BILLING THE MODEL ON EVERY RETURN TO THE TAB.

              Measured with the AI request intercepted so every generation fails, counters read off
              the wire for ONE snapshot: after mount `chat=1 material=1`; after one tab round-trip
              (Decision Outputs → AI Narrative) `chat=2 material=2`; after a second, `chat=3
              material=3`. Three model calls for one snapshot in eleven seconds, with the
              narrative-persisting PUT at zero throughout. A Manager's whole daily AI allowance is
              THREE requests, so two tab switches after an AI outage exhausted a manager's day.

              Mechanism: the panel's mount effect is `if (!narrative && !readOnly) generate()`, and
              a failed generation deliberately persists NOTHING — correctly, because a fabricated
              performance narrative in someone's record is worse than an empty one — so `!narrative`
              stays true forever. Conditional rendering then re-ran that mount effect on every
              return to the tab. (This was introduced BY the Wave-1 fix: before it, a fabricated
              English fallback was lifted and autosaved, which made `!narrative` false and masked
              the re-fire. Deleting the fabrication was right; nothing recorded the attempt.)

              THE FIX KEEPS BOTH PROPERTIES, and neither may be traded for the other:
                1. a failed generation still persists no fabricated narrative — nothing below
                   introduces a fallback, a placeholder or an empty-string save; and
                2. the automatic generation fires ONCE PER SNAPSHOT, not once per mount, because the
                   panel now mounts once per snapshot (the wrapper `key` above) and simply hides.
                   The panel's own Retry button is user-initiated and still works — it is the
                   SILENT, AUTOMATIC re-fire that was the defect.

              🔴 CO-441 CLOSED THE OTHER HALF OF THE SAME HOLE. "Once per snapshot" is only cheap
              while the number of snapshots opened is small, and the history list made it
              unbounded: every chip click is a different snapshot id, so a different `key`, so a
              fresh mount and a fresh generation. The mount effect is therefore gated on
              `autoGenerate` below, which only Generate sets. Both properties above still hold; a
              third has been added — an AUTOMATIC generation now requires somebody to have ASKED
              for this month.

              The hide-don't-unmount trick was originally chosen over lifting a flag into page
              state, on the grounds that a flag needs a new prop in `NarrativeCompilationPanel.tsx`
              and the lane at the time did not own that file. CO-441 shows the two are not
              alternatives: `key`-per-snapshot fixes the TAB-SWITCH re-fire and only that, so the
              `autoGenerate` prop was added as well. Keep BOTH — dropping either reopens one half.

              The hiding is deliberately applied to THIS panel ONLY — the other four are left
              conditional, because eagerly mounting `ComparativeContextPanel` would fire its fetch
              on every snapshot open whether or not anyone opens the tab.

              The `hidden` ATTRIBUTE (not only the class) is what takes the subtree out of the
              accessibility tree and the tab order; the class is belt-and-braces against any future
              rule that sets a `display` on this element and would silently defeat the attribute.
            */}
            {selectedEmployee && (
              <div
                hidden={activeTab !== 'narrative'}
                className={activeTab === 'narrative' ? undefined : 'hidden'}
                data-testid="snapshot-panel-narrative"
              >
                <NarrativeCompilationPanel
                  employee={selectedEmployee}
                  reviewData={reviewData}
                  readOnly={isReadOnly}
                  /* 🔴 CO-441 — the automatic model call is OPT-IN, and only Generate opts in.
                     The wrapper `key` above remounts this panel on every change of snapshot, so
                     the history list would otherwise re-fire the mount-time generation once per
                     click. Compared against the snapshot ON SCREEN so a stale arming cannot leak
                     into a different month. */
                  autoGenerate={
                    reviewData.snapshotId != null && reviewData.snapshotId === autoNarrativeSnapshotId
                  }
                  onNarrativeUpdate={(narrative) =>
                    // `NarrativeData` is an interface, so it needs the widening step to land in the
                    // page's `Record<string, unknown>` slot. The page stores these blobs opaquely
                    // and only ever re-serializes them.
                    setReviewData((prev) =>
                      prev ? { ...prev, narrative: narrative as unknown as Record<string, unknown> } : prev,
                    )
                  }
                />
              </div>
            )}
            {activeTab === 'strengths-skills' && selectedEmployee && (
              <SurfedStrengthsSkillsPanel
                employee={selectedEmployee}
                reviewData={reviewData}
                readOnly={isReadOnly}
                onStrengthsValidationUpdate={(validation) =>
                  setReviewData((prev) => (prev ? { ...prev, strengths: validation } : prev))
                }
              />
            )}
            {activeTab === 'prompts' && selectedEmployee && (
              <NarrativePromptsPanel
                employee={selectedEmployee}
                reviewData={reviewData}
                readOnly={isReadOnly}
                onPromptsUpdate={(prompts) =>
                  setReviewData((prev) => (prev ? { ...prev, prompts } : prev))
                }
              />
            )}
            {activeTab === 'comparative' && selectedEmployee && (
              <ComparativeContextPanel
                employee={selectedEmployee}
                reviewData={reviewData}
                onComparativeUpdate={(comparative) =>
                  setReviewData({ ...reviewData, comparative })
                }
              />
            )}
            {activeTab === 'decisions' && selectedEmployee && (
              <DecisionOutputsPanel
                employee={selectedEmployee}
                reviewData={reviewData}
                readOnly={isReadOnly}
                onDecisionsUpdate={(decisions) =>
                  // Same widening as the narrative above: `DecisionState` is an interface.
                  setReviewData((prev) =>
                    prev ? { ...prev, decisions: decisions as unknown as Record<string, unknown> } : prev,
                  )
                }
              />
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceSnapshot;
